/**
 * 3D Preview using Three.js (Lazy Loaded for Performance)
 * @license GPL-3.0-or-later
 */

import {
  AmbientLight,
  AxesHelper,
  Box3,
  BoxHelper,
  BufferGeometry,
  CanvasTexture,
  Color,
  ColorManagement,
  DirectionalLight,
  DoubleSide,
  EdgesGeometry,
  Float32BufferAttribute,
  GridHelper,
  Group,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  LineSegments,
  LinearSRGBColorSpace,
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  OrthographicCamera,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Sprite,
  SpriteMaterial,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import {
  classifyInnerFaces,
  offFaceCount,
  parseOFF,
  parseOFFWithAlpha,
} from './mesh-extras.js';
import { buildAxisTriadOverlay } from './axis-triad-overlay.js';
import { normalizeHexColor } from './color-utils.js';
import { get2DStylePalette } from './state-colors.js';
import {
  announceCameraAction as announceCamera,
  announceImmediate,
} from './announcer.js';
import {
  STORAGE_KEY_MEASUREMENTS,
  STORAGE_KEY_GRID,
  STORAGE_KEY_GRID_SIZE,
  STORAGE_KEY_CUSTOM_GRID_PRESETS,
  STORAGE_KEY_GRID_COLOR,
  STORAGE_KEY_GRID_OPACITY,
  STORAGE_KEY_AUTO_BED,
  STORAGE_KEY_ZOOM_TO_CURSOR,
  STORAGE_KEY_VIEWPORT_SCHEME,
  STORAGE_KEY_LOD_WARNING_DISMISSED,
  STORAGE_KEY_HFM_GRID,
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
} from './storage-keys.js';
// The per-interface viewing preferences read and write through the scoped
// facade: one saved copy per interface. Camera pose stays live shared
// state, and custom grid presets stay app-level (a saved library, not a
// viewing state).
import {
  readScopedPref,
  writeScopedPref,
  removeScopedPref,
} from './ui-scoped-prefs.js';

// Disable Three.js color management to match desktop OpenSCAD's
// non-linear-aware OpenGL pipeline. OpenSCAD passes sRGB colors
// directly through lighting without linearization or gamma correction.
ColorManagement.enabled = false;

/** Default grid config — 220×220mm matches popular mid-range FDM printers (Creality K1C, FlashForge Adventurer 5M Pro) */
const DEFAULT_GRID_CONFIG = { widthMm: 220, heightMm: 220 };

/**
 * How far above a face the reference overlay sits when it is placed on that
 * face. Exactly coincident planes z-fight, and the overlay is meant to be
 * traced against the surface, so it has to win.
 */
const OVERLAY_SURFACE_EPSILON_MM = 0.25;

/** Scratch target for renderer.getSize() in the triad pass (r162 requires one). */
const _triadSizeScratch = new Vector2();

export function isThreeJsLoaded() {
  return true;
}

/**
 * @returns {object} Object containing Three.js classes for external consumers
 *                   (e.g. display-options-controller).
 */
export function getThreeModule() {
  return {
    AxesHelper,
    Box3,
    BoxHelper,
    BufferGeometry,
    EdgesGeometry,
    Float32BufferAttribute,
    Group,
    Line,
    LineBasicMaterial,
    // The dashed negative axis halves. A consumer asking for a class this
    // object does not carry fails at runtime, so every class the overlays use
    // is listed here.
    LineDashedMaterial,
    LineSegments,
    Vector3,
    // The axis-tick overlay rasterises its distance labels onto a canvas and
    // hangs them in the scene as sprites. Without these three it would throw
    // "three.CanvasTexture is not a constructor" before the group is returned,
    // and the whole overlay, tick lines included, would be lost. Its unit
    // tests inject a mock module that does define them, so they cannot catch
    // a missing entry here.
    CanvasTexture,
    Sprite,
    SpriteMaterial,
  };
}

/**
 * LOD (Level of Detail) configuration
 */
/**
 * Above this many triangles the cavity-tint classification and the edge
 * segments are computed in a worker and applied when they arrive; below it
 * they run here, in a few milliseconds. The two cost about 6 microseconds
 * per triangle on the development machine and four times that at a 4x CPU
 * slowdown, so 10,000 triangles is about 60 ms there and 250 ms on a slow
 * device, the most a load may hold the page. A 211,700-triangle design
 * held the page for 6.4 s at 4x when this ran on the main thread.
 */
export const SYNC_MESH_EXTRAS_MAX_TRIANGLES = 10_000;

const LOD_CONFIG = {
  vertexWarningThreshold: 100000, // Warn above 100K vertices
  vertexCriticalThreshold: 500000, // Critical warning above 500K vertices
  showWarning: true,
};

/**
 * Theme-aware color scheme for 3D preview
 */
// Cornfield front-face color: OpenSCAD src/glview/ColorMap.cc default ctor
// OPENCSG_FACE_FRONT_COLOR = #F9D72C.
// Exported for reference and tests. The light theme does not use the
// Cornfield pair directly: #f9d72c measures 1.3:1 against the #f5f5f5
// viewer background and fails WCAG 2.2 SC 1.4.11 (see PREVIEW_COLORS
// below).
export const CORNFIELD_FRONT_COLOR = 0xf9d72c;

// Cornfield back-face (CUTOUT) color: OpenSCAD src/glview/ColorMap.cc default ctor
// OPENCSG_FACE_BACK_COLOR = #9DCB51
export const CORNFIELD_BACK_COLOR = 0x9dcb51;

// Desktop Phong exponent: OpenSCAD src/glview/GLView.cc line 324 (master),
// src/GLView.cc line 351 (2021):
// glMateriali(GL_FRONT_AND_BACK, GL_SHININESS, 64).
// Specular is black (0,0,0) so shininess has no visible effect, but aligning
// for forward compatibility if specular is ever enabled.
export const DESKTOP_SHININESS = 64;

/**
 * How far one discrete zoom activation moves the camera, in the units
 * zoomCamera() takes — roughly 30% per press.
 *
 * The single source for every button, menu item and keyboard shortcut that
 * zooms by a fixed step, so every surface moves the camera by the same
 * amount. A step of 1 (about 2%) would be effectively imperceptible and
 * punishing for switch users.
 * Continuous inputs such as the gamepad axis scale their own delta instead.
 */
export const CAMERA_ZOOM_STEP = 15;

/**
 * How wide a size label is, in model millimeters, for a model whose longest
 * side is `span`. A fixed 20 mm label hid a 22 mm charm under three of them;
 * about a third of the longest side keeps the three clear of each other and
 * of the model, never smaller than 4 mm or wider than 40 mm.
 * @param {number} span - the model's longest side, mm
 * @returns {number} label width, mm
 */
export function dimensionLabelWidth(span) {
  const s = Number(span) || 0;
  return Math.min(40, Math.max(4, s * 0.35));
}

// The model is a non-text graphical object, so `model`/`modelBack` must
// reach 3:1 against `background` (WCAG 2.2 SC 1.4.11). The `edges`
// overlay lines render 1px wide, so per W3C Low Vision Task Force
// guidance for thin strokes they are held to the 4.5:1 text threshold
// against the model color they are drawn on. All ratios are enforced by
// tests/unit/preview-colors-contrast.test.js.
//
// The light theme's model colors are the desktop Cornfield front/back pair
// (#f9d72c / #9dcb51) darkened in the same hues (3.5:1 / 3.8:1): the
// originals measure 1.3:1 and 1.7:1 against the #f5f5f5 background and
// fail SC 1.4.11. Desktop parity for the other themes is unverified, so
// accessibility wins here.
export const PREVIEW_COLORS = {
  light: {
    background: 0xf5f5f5,
    gridPrimary: 0xcccccc,
    gridSecondary: 0xe0e0e0,
    model: 0x9a8200, // darkened Cornfield gold, 3.5:1 vs background
    modelBack: 0x5a8a22, // darkened Cornfield green, 3.8:1 vs background
    edges: 0x020617, // 5.4:1 vs model
    ambientLight: 0xffffff,
  },
  dark: {
    background: 0x1a1a1a,
    gridPrimary: 0x404040,
    gridSecondary: 0x2d2d2d,
    model: 0x4d9fff,
    modelBack: 0x3d8a44, // green tint for dark background (unverified)
    edges: 0x0d1117, // 7.0:1 vs model
    ambientLight: 0xffffff,
  },
  'light-hc': {
    background: 0xffffff,
    gridPrimary: 0x000000,
    gridSecondary: 0x666666,
    model: 0x0052cc,
    modelBack: 0x338a33, // high-contrast green (unverified)
    edges: 0xffffff, // 6.8:1 vs model
    ambientLight: 0xffffff,
  },
  'dark-hc': {
    background: 0x000000,
    gridPrimary: 0xffffff,
    gridSecondary: 0x999999,
    model: 0x66b3ff,
    modelBack: 0x66cc66, // high-contrast green for dark (unverified)
    edges: 0x000000, // 9.5:1 vs model
    ambientLight: 0xffffff,
  },
  // Green phosphor (dark theme mono variant)
  mono: {
    background: 0x000000,
    gridPrimary: 0x00ff00,
    gridSecondary: 0x00aa00,
    model: 0x00ff00,
    modelBack: 0x00aa00, // dimmer green for intensity distinction (unverified)
    edges: 0x003300, // 10.4:1 vs model, keeps the phosphor palette
    ambientLight: 0x00ff00,
  },
  // Amber phosphor (light theme mono variant)
  'mono-light': {
    background: 0x000000,
    gridPrimary: 0xffb000,
    gridSecondary: 0xcc8c00,
    model: 0xffb000,
    modelBack: 0xcc8c00, // dimmer amber for intensity distinction (unverified)
    edges: 0x332200, // 8.4:1 vs model, keeps the phosphor palette
    ambientLight: 0xffb000,
  },
  // Green phosphor high-contrast (wider grid contrast ratio)
  'mono-hc': {
    background: 0x000000,
    gridPrimary: 0x00ff00,
    gridSecondary: 0x003300,
    model: 0x33ff33,
    modelBack: 0x00cc00, // dimmer green phosphor HC (unverified)
    edges: 0x001a00, // 13.5:1 vs model
    ambientLight: 0x00ff00,
  },
  // Amber phosphor high-contrast (wider grid contrast ratio)
  'mono-light-hc': {
    background: 0x000000,
    gridPrimary: 0xffb000,
    gridSecondary: 0x4d3500,
    model: 0xffc233,
    modelBack: 0xcc9a00, // dimmer amber phosphor HC (unverified)
    edges: 0x261a00, // 10.6:1 vs model
    ambientLight: 0xffb000,
  },
  // Classic mode: the desktop's Cornfield viewport background
  // (#FFFFE5 — OpenSCAD src/glview/ColorMap.cc BACKGROUND_COLOR).
  // The model pair stays the accessibility-tuned one; on cornfield it
  // measures better than on the light theme's #f5f5f5 (3.7:1 / 4.1:1 vs
  // 3.5:1 / 3.8:1), so fidelity and contrast agree here. Classic ignores
  // theme and high contrast entirely, so it has no -hc sibling.
  classic: {
    background: 0xffffe5,
    gridPrimary: 0xb9b9a1,
    gridSecondary: 0xd6d6bf,
    model: 0x9a8200,
    modelBack: 0x5a8a22,
    edges: 0x020617,
    // Upstream AXES_COLOR, verbatim: Cornfield is hardcoded in
    // src/colormap.cc line 40 at tag openscad-2021.01 (no cornfield.json
    // exists). 20.69:1 vs background — passes SC 1.4.11 untouched.
    axes: 0x000000,
    ambientLight: 0xffffff,
  },

  // --- Desktop viewport color schemes (Preferences ▸ 3D View) ---
  //
  // Transcribed from OpenSCAD at tag `openscad-2021.01`: Cornfield is the
  // hardcoded default in `src/colormap.cc` (lines 39-47, no JSON exists for
  // it) and ships above as `classic`; the rest are
  // `color-schemes/render/<name>.json`. `monotone.json` is deliberately
  // absent — it carries `"show-in-gui": false`, which is why the desktop's
  // own list shows nine JSON schemes plus the built-in Cornfield.
  //
  // Backgrounds are verbatim. Model and edge colors are tuned the minimum
  // distance along lightness — hue and saturation untouched, so a scheme
  // still reads as itself — until `model`/`modelBack` reach 3:1 against the
  // background (SC 1.4.11) and `edges` reaches 4.5:1 against the model.
  // Measured verbatim, only Starnight and DeepOcean already passed; upstream
  // Cornfield is 1.40:1 and 1.86:1. Every ratio below is asserted by
  // tests/unit/preview-colors-contrast.test.js, so a hand edit that breaks
  // one fails the suite.
  //
  // `axes` is each scheme's `axes-color`, verbatim from the same upstream
  // files: the axis lines and tick labels paint with it instead of
  // the app's text token, so a Forge dark theme cannot bleed a light
  // foreground into a light scheme. All ten upstream values already pass
  // SC 1.4.11's 3:1 against their own backgrounds (sunset is the narrowest
  // at 3.20:1), so none needed tuning. Also asserted by the contrast suite.
  //
  // Where a comment says "unchanged" the value is upstream's.
  metallic: {
    background: 0xaaaaff, // upstream, verbatim
    gridPrimary: 0x7c7cd1,
    gridSecondary: 0x9393e8,
    model: 0x3c3cff, // 3.02:1 vs background (upstream #ddddff, lightness -31.5%)
    modelBack: 0xa519a5, // 3.02:1 (upstream #dd22dd, lightness -12.6%)
    edges: 0xffcdcd, // 4.53:1 vs model (upstream #ff0000, lightness +40.1%)
    axes: 0x222233, // upstream axes-color, verbatim (7.36:1 vs background)
    ambientLight: 0xffffff,
  },
  sunset: {
    background: 0xaa4444, // upstream, verbatim
    gridPrimary: 0xd87272,
    gridSecondary: 0xc15b5b,
    model: 0xffaaaa, // 3.20:1 vs background (upstream, unchanged)
    modelBack: 0x320d13, // 3.01:1 (upstream #882233, lightness -21%)
    edges: 0xa30000, // 4.53:1 vs model (upstream #ff0000, lightness -18%)
    axes: 0x220d0d, // upstream axes-color, verbatim (3.20:1 vs background)
    ambientLight: 0xffffff,
  },
  starnight: {
    background: 0x000000, // upstream, verbatim
    gridPrimary: 0x2e2e2e,
    gridSecondary: 0x171717,
    model: 0xffffe0, // 20.63:1 vs background (upstream, unchanged)
    modelBack: 0x00ffff, // 16.75:1 (upstream, unchanged)
    edges: 0x0000ff, // 8.44:1 vs model (upstream, unchanged)
    axes: 0xe5e5e5, // upstream axes-color, verbatim (16.67:1 vs background)
    ambientLight: 0xffffff,
  },
  beforedawn: {
    background: 0x333333, // upstream, verbatim
    gridPrimary: 0x616161,
    gridSecondary: 0x4a4a4a,
    model: 0xcccccc, // 7.87:1 vs background (upstream, unchanged)
    modelBack: 0x6571e0, // 3.00:1 (upstream #5563dd, lightness +3.7%)
    edges: 0xb20000, // 4.52:1 vs model (upstream #ff0000, lightness -15.1%)
    axes: 0xc1c1c1, // upstream axes-color, verbatim (7.02:1 vs background)
    ambientLight: 0xffffff,
  },
  nature: {
    background: 0xfafafa, // upstream, verbatim
    gridPrimary: 0xcccccc,
    gridSecondary: 0xe3e3e3,
    model: 0x16a085, // 3.14:1 vs background (upstream, unchanged)
    modelBack: 0x36a631, // 3.02:1 (upstream #dbf4da, lightness -48.3%)
    edges: 0x580000, // 4.52:1 vs model (upstream #ff0000, lightness -32.7%)
    axes: 0x323232, // upstream axes-color, verbatim (12.28:1 vs background)
    ambientLight: 0xffffff,
  },
  deepocean: {
    background: 0x333333, // upstream, verbatim
    gridPrimary: 0x616161,
    gridSecondary: 0x4a4a4a,
    model: 0xeeeeee, // 10.89:1 vs background (upstream, unchanged)
    modelBack: 0x0babc8, // 4.62:1 (upstream, unchanged)
    edges: 0x0000ff, // 7.41:1 vs model (upstream, unchanged)
    axes: 0xc1c1c1, // upstream axes-color, verbatim (7.02:1 vs background)
    ambientLight: 0xffffff,
  },
  solarized: {
    background: 0xfdf6e3, // upstream, verbatim
    gridPrimary: 0xcfc8b5,
    gridSecondary: 0xe6dfcc,
    model: 0xb58800, // 3.00:1 vs background (upstream, unchanged)
    modelBack: 0x882233, // 8.38:1 (upstream, unchanged)
    edges: 0x342700, // 4.52:1 vs model (upstream #b58800, lightness -25.2%)
    axes: 0x191816, // upstream axes-color, verbatim (16.45:1 vs background)
    ambientLight: 0xffffff,
  },
  tomorrow: {
    background: 0xf8f8f8, // upstream, verbatim
    gridPrimary: 0xcacaca,
    gridSecondary: 0xe1e1e1,
    model: 0x4271ae, // 4.70:1 vs background (upstream, unchanged)
    modelBack: 0xe0720a, // 3.00:1 (upstream #f5871f, lightness -8.2%)
    // Lightened, not darkened: against this mid-tone model even pure black
    // only reaches ~4.2:1, so no dark edge can satisfy the thin-stroke
    // threshold here.
    edges: 0xf3f3f3, // 4.50:1 vs model (upstream #4d4d4c, lightness +65.2%)
    axes: 0x181818, // upstream axes-color, verbatim (16.72:1 vs background)
    ambientLight: 0xffffff,
  },
  'tomorrow-night': {
    background: 0x1d1f21, // upstream, verbatim
    gridPrimary: 0x4b4d4f,
    gridSecondary: 0x343638,
    model: 0x81a2be, // 6.18:1 vs background (upstream, unchanged)
    modelBack: 0xde935f, // 6.65:1 (upstream, unchanged)
    edges: 0x333634, // 4.57:1 vs model (upstream #c5c8c6, lightness -57.1%)
    axes: 0xe8e8e8, // upstream axes-color, verbatim (13.49:1 vs background)
    ambientLight: 0xffffff,
  },
};

/**
 * The desktop's 3D View color-scheme list, in its own order (the JSON
 * `index` field, the order the desktop's Preferences list shows).
 *
 * `colors` names the PREVIEW_COLORS entry each one paints with. Cornfield
 * maps to `classic` rather than a tenth near-duplicate: `classic` already is
 * the Cornfield background with an accessibility-tuned model pair, and it
 * holds more contrast headroom (3.72:1 / 4.06:1) than a fresh
 * minimum-distance tune would produce.
 */
export const VIEWPORT_SCHEMES = Object.freeze([
  { id: 'cornfield', label: 'Cornfield', colors: 'classic' },
  { id: 'metallic', label: 'Metallic', colors: 'metallic' },
  { id: 'sunset', label: 'Sunset', colors: 'sunset' },
  { id: 'starnight', label: 'Starnight', colors: 'starnight' },
  { id: 'beforedawn', label: 'BeforeDawn', colors: 'beforedawn' },
  { id: 'nature', label: 'Nature', colors: 'nature' },
  { id: 'deepocean', label: 'DeepOcean', colors: 'deepocean' },
  { id: 'solarized', label: 'Solarized', colors: 'solarized' },
  { id: 'tomorrow', label: 'Tomorrow', colors: 'tomorrow' },
  { id: 'tomorrow-night', label: 'Tomorrow Night', colors: 'tomorrow-night' },
]);

/** The scheme Classic opens with — the desktop's own default. */
export const DEFAULT_VIEWPORT_SCHEME = 'cornfield';

/** @param {string} id @returns {string} PREVIEW_COLORS key for that scheme */
export function schemeColorsKey(id) {
  return (
    VIEWPORT_SCHEMES.find((s) => s.id === id)?.colors ??
    VIEWPORT_SCHEMES.find((s) => s.id === DEFAULT_VIEWPORT_SCHEME).colors
  );
}

const VIEWPORT_SCHEME_KEYS = new Set(VIEWPORT_SCHEMES.map((s) => s.colors));

/**
 * Whether a PREVIEW_COLORS key is a Classic viewport scheme rather than an
 * app theme. These have no `-hc` siblings, so nothing may suffix them.
 * @param {string} key
 * @returns {boolean}
 */
export function isViewportSchemeKey(key) {
  return VIEWPORT_SCHEME_KEYS.has(key);
}

// No render-state tints: desktop OpenSCAD uses the same color scheme for
// both F5 preview and F6 render and does not tint models by render quality
// or laser mode. Model color is colorOverride (SCAD-derived), else
// PREVIEW_COLORS[theme].model.

export class PreviewManager {
  constructor(container, options = {}) {
    this.container = container;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.mesh = null;
    this.gridHelper = null;
    this.animationId = null;
    this.currentTheme = options.theme || 'light';
    this.highContrast = options.highContrast || false;
    this.colorOverride = null;
    this.colorOverrideEnabled = false;
    this.appearanceOverrideEnabled = false;

    // Measurements
    this.measurementsEnabled = this.loadMeasurementPreference();
    this.measurementHelpers = null; // Group containing all measurement visuals
    this.dimensions = null; // { x, y, z, volume }

    // Grid visibility
    this.gridEnabled = this.loadGridPreference();
    this.altViewGridActive = false;

    // Grid size (mm) — configurable to match printer bed
    this.gridConfig = this.loadGridSizePreference();

    // Custom grid color override (null = use theme default)
    this.gridColorOverride = this.loadGridColorPreference();

    // Grid line opacity (10–100, default 100 = fully opaque)
    this.gridOpacity = this.loadGridOpacityPreference();

    // Camera projection mode (perspective or orthographic)
    this.projectionMode = 'perspective';
    this.orthoCamera = null; // Lazy-created orthographic camera

    // Auto-bed: place object on Z=0 build plate
    this.autoBedEnabled = this.loadAutoBedPreference();

    // Mouse-wheel zoom focal point: when true, zoom centers on the cursor;
    // when false, zoom centers on the orbit target. Defaults to true, zoom
    // toward the cursor.
    this.zoomToCursorEnabled = this.loadZoomToCursorPreference();

    // Chosen desktop viewport color scheme (Preferences ▸ 3D View). Read
    // before the first detectTheme() so Classic opens on the saved choice
    // rather than flashing Cornfield and correcting itself.
    this.viewportScheme = this.loadViewportScheme();

    // Rotation centering: temporarily center object at origin for better rotation
    this.autoBedOffset = 0; // Z offset applied by auto-bed
    this.rotationCenteringEnabled = false; // Whether rotation centering is active

    // 2D preview mode (native SVG display vs Three.js 3D canvas)
    this._is2DPreviewActive = false;

    // Render hooks for extensibility
    this._renderOverride = null;
    this._resizeHook = null;
    // Corner XYZ triad: a second render pass in the default branch
    // of animate(). Follows the Axes display option (desktop: showSmallaxes
    // runs iff showaxes).
    this._axisTriad = null;
    this._postLoadHook = null; // Called after STL is loaded
    this._postLoadListeners = []; // Multi-listener post-load event
    // The worker that computes a big mesh's extras, and the token of
    // the mesh whose answer is still wanted (a mesh replaced before its
    // answer arrives has its answer dropped).
    this._extrasWorker = null;
    this._extrasToken = 0;
    this._pendingOFF = new Map();
    this._themeChangeListeners = []; // Multi-listener theme-change event

    // Reference overlay (screenshot/SVG image plane under the model)
    this.referenceOverlay = null; // THREE.Mesh for the overlay plane
    this.referenceTexture = null; // THREE.Texture for the image
    this.overlayConfig = {
      enabled: false,
      opacity: 1.0,
      offsetX: 0, // mm
      offsetY: 0, // mm
      rotationDeg: 0,
      width: 200, // mm (default; replaced by SVG physical size or explicit sizing)
      height: 150, // mm
      // The effective Z, in mm. Derived from zPreset except when the
      // preset is 'custom', where the person typed it.
      zPosition: -0.25, // Slightly below Z=0 build plate (avoid z-fighting with grid)
      // Which surface the overlay is meant to sit against. A raw number is a
      // poor control here: -0.25 means nothing to someone deciding "I want to
      // trace onto the top of the charm". 'model-top' is recomputed whenever
      // the model changes, so it follows the object rather than freezing at
      // whatever the height happened to be when it was chosen.
      zPreset: 'under-plate',
      zCustomMm: 0,
      lockAspect: true,
      intrinsicAspect: null, // Width/height ratio from source image
      sourceFileName: null, // Name of the file used as overlay source
      svgColor: null, // Recolor SVG strokes/fills (null = original colors)
    };

    // Overlay measurements (dimension lines on the overlay)
    this.overlayMeasurementsEnabled = false;
    this.overlayMeasurementHelpers = null; // Group containing overlay measurement visuals

    // Resize state tracking for view preservation
    this._lastAspect = null; // Previous aspect ratio for comparison
    this._lastContainerWidth = 0; // Track container dimensions
    this._lastContainerHeight = 0;
    this._resizeDebounceId = null; // Debounce timer for resize handling

    // Configuration for resize behavior
    this._resizeConfig = {
      // Threshold for "significant" aspect ratio change (0.15 = 15%)
      aspectChangeThreshold: 0.15,
      // Debounce delay for resize stabilization (ms)
      debounceDelay: 100,
      // Whether to adjust camera distance on aspect changes
      adjustCameraOnResize: true,
    };
  }

  /**
   * Initialize Three.js scene
   * @returns {Promise<void>}
   */
  async init() {
    // Clear container (preserving/recreating #rendered2dPreview for 2D SVG display)
    this.container.innerHTML = '';

    // Recreate the 2D SVG preview surface that init() just destroyed
    if (!document.getElementById('rendered2dPreview')) {
      const preview2d = document.createElement('div');
      preview2d.id = 'rendered2dPreview';
      preview2d.className = 'rendered-2d-preview hidden';
      preview2d.setAttribute('role', 'img');
      preview2d.setAttribute('aria-label', 'Rendered 2D SVG preview');
      this.container.appendChild(preview2d);
    }

    // And the drawing editor's surface, for the same reason: the
    // markup in index.html is the honest picture of the container, and the
    // wipe above takes it with everything else.
    if (!document.getElementById('drawingEditorSurface')) {
      const surface = document.createElement('div');
      surface.id = 'drawingEditorSurface';
      surface.className = 'drawing-editor-surface hidden';
      surface.setAttribute('role', 'region');
      surface.setAttribute('aria-label', 'Drawing editor');
      surface.hidden = true;
      this.container.appendChild(surface);
    }

    // Detect initial theme
    this.currentTheme = this.detectTheme();
    const colors = PREVIEW_COLORS[this.currentTheme];

    // Create scene
    this.scene = new Scene();
    this.scene.background = new Color(colors.background);

    // Create camera with OpenSCAD-compatible Z-up coordinate system
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    this.camera = new PerspectiveCamera(45, width / height, 0.1, 10000);

    // Initialize resize tracking state
    this._lastAspect = width / height;
    this._lastContainerWidth = width;
    this._lastContainerHeight = height;

    // Set Z as the up axis (OpenSCAD uses Z-up, Three.js defaults to Y-up).
    // This must run BEFORE OrbitControls is constructed below: the controls
    // capture their orbit frame from camera.up once and never re-read it.
    this.camera.up.set(...PreviewManager.WORLD_UP);

    // Position camera for OpenSCAD-style diagonal view (looking at origin from front-right-above)
    // This mimics OpenSCAD's default "Diagonal" view orientation
    this.camera.position.set(...PreviewManager.DEFAULT_CAMERA_POSITION);

    // Create renderer — WebGL may be unavailable in headless browsers.
    // When that happens, geometry parsing (loadOFF / loadSTL) still works;
    // only the visual canvas is disabled.
    //
    // Three is pinned to ^0.162.0 because r163 removed WebGL 1
    // support. Browsers with WebGL 2 disabled (Firefox hardware blocklist,
    // privacy-hardened profiles) can still render via the WebGL 1 fallback
    // in r162. Do not upgrade three past 0.162.x without an alternative
    // fallback for those users.
    try {
      this.renderer = new WebGLRenderer({ antialias: true });
      this.renderer.outputColorSpace = LinearSRGBColorSpace;
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.container.appendChild(this.renderer.domElement);
      this.renderer.domElement.setAttribute('tabindex', '0');
      this.renderer.domElement.setAttribute('aria-label', '3D preview canvas');
      this.renderer.domElement.addEventListener('click', () => {
        this.renderer.domElement.focus();
      });
    } catch (webglError) {
      console.warn(
        '[Preview] WebGL renderer unavailable — 3D canvas disabled:',
        webglError.message
      );
      this.renderer = null;
      this._showWebGLUnavailableNotice();
    }

    // Lighting matched to desktop OpenSCAD's GLView::setupLight().
    // OpenSCAD sets light positions AFTER the camera (modelview) transform,
    // so they are in view space — they move with the camera like headlights.
    // We replicate this by parenting directional lights to the camera.
    //
    // Three.js MeshPhongMaterial uses BRDF_Lambert which divides diffuse by π.
    // OpenSCAD's OpenGL pipeline has no such divisor (simple color * NdotL).
    // All intensities are scaled by π to cancel the BRDF divisor: ambient
    // 0.2*π ≈ 0.628. The directional pair carries a further ×1.5, measured
    // against the desktop: with the π-cancel alone, A/B captures at a pinned
    // desktop pose render visibly darker than OpenSCAD 2021.01 even though the
    // preview mesh wears the WASM-baked upstream colors verbatim. The
    // remaining gap is three's lighting evaluation, not color handling, so the
    // level is calibrated here and nowhere else. The user brightness/contrast
    // sliders stay neutral multipliers on top of these bases.
    const piAmbient = 0.2 * Math.PI;
    const piDirectional = 1.5 * Math.PI;

    this.ambientLight = new AmbientLight(colors.ambientLight, piAmbient);
    this.scene.add(this.ambientLight);

    // Camera must be in the scene graph for child lights to render
    this.scene.add(this.camera);

    this.directionalLight1 = new DirectionalLight(0xffffff, piDirectional);
    this.directionalLight1.position.set(-1, 1, 1);
    this.camera.add(this.directionalLight1);
    this.camera.add(this.directionalLight1.target);

    this.directionalLight2 = new DirectionalLight(0xffffff, piDirectional);
    this.directionalLight2.position.set(1, -1, -1);
    this.camera.add(this.directionalLight2);
    this.camera.add(this.directionalLight2.target);

    // Store base intensities for brightness/contrast controls
    this.baseLightIntensities = {
      ambient: piAmbient,
      dir1: piDirectional,
      dir2: piDirectional,
    };
    this._brightnessScale = 1;
    this._contrastFactor = 1;

    // Add grid helper on XY plane (OpenSCAD's ground plane)
    // GridHelper by default creates a grid on XZ plane (Y-up), so we rotate it for Z-up
    const gridColors = this._resolveGridColors();
    this.gridHelper = this._createGridHelper(gridColors);
    // Rotate grid from XZ plane to XY plane (Z-up coordinate system)
    this.gridHelper.rotation.x = Math.PI / 2;
    // Apply saved grid visibility preference
    this.gridHelper.visible = this.gridEnabled;
    this._applyGridOpacity();
    this.scene.add(this.gridHelper);

    // Add orbit controls (OpenSCAD-style) — requires a renderer DOM element
    if (this.renderer) {
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.screenSpacePanning = true;
      this.controls.minDistance = 10;
      this.controls.maxDistance = 1000;
      this.controls.zoomToCursor = this.zoomToCursorEnabled;

      this.setupKeyboardControls();
      this._setupPoleCrossing();
    }

    // Handle window resize with view preservation
    this.handleResize = () => {
      // While the charm view has the canvas fitted to the editor's own window,
      // that window is what a resize has to re-measure. Sizing to the
      // container here would hand the canvas back mid-session.
      if (this._charmHost) {
        this._sizeCanvasToCharmHost();
        return;
      }
      const width = this.container.clientWidth;
      const height = this.container.clientHeight;

      // Skip if dimensions are invalid or unchanged
      if (width <= 0 || height <= 0) return;
      if (
        width === this._lastContainerWidth &&
        height === this._lastContainerHeight
      ) {
        return;
      }

      const newAspect = width / height;
      const previousAspect = this._lastAspect || newAspect;

      // Update perspective camera aspect (always kept current for projection toggling)
      this.camera.aspect = newAspect;
      this.camera.updateProjectionMatrix();

      // Update orthographic camera frustum if it exists
      if (this.orthoCamera) {
        const frustumHeight =
          (this.orthoCamera.top - this.orthoCamera.bottom) /
          (this.orthoCamera.zoom || 1);
        this.orthoCamera.left = (frustumHeight * newAspect) / -2;
        this.orthoCamera.right = (frustumHeight * newAspect) / 2;
        this.orthoCamera.updateProjectionMatrix();
      }

      if (this.renderer) this.renderer.setSize(width, height);

      // Adjust camera to maintain model's relative position when aspect changes significantly
      if (
        this.mesh &&
        this._resizeConfig.adjustCameraOnResize &&
        this._lastAspect !== null
      ) {
        const aspectRatioChange =
          Math.abs(newAspect - previousAspect) / previousAspect;

        // Only adjust for significant changes (configurable threshold)
        if (aspectRatioChange > this._resizeConfig.aspectChangeThreshold) {
          this._adjustCameraForAspectChange(previousAspect, newAspect);
        }
      }

      // Store current state for next comparison
      this._lastAspect = newAspect;
      this._lastContainerWidth = width;
      this._lastContainerHeight = height;

      // Call resize hook if set (for alternate renderers)
      this._resizeHook?.({ width, height });
    };

    // Debounced resize handler for smoother performance
    this._debouncedResize = () => {
      if (this._resizeDebounceId) {
        cancelAnimationFrame(this._resizeDebounceId);
      }
      this._resizeDebounceId = requestAnimationFrame(() => {
        this.handleResize();
        this._resizeDebounceId = null;
      });
    };

    window.addEventListener('resize', this._debouncedResize);

    // Add ResizeObserver for container size changes (e.g., split panel resize)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this._debouncedResize();
      });
      this.resizeObserver.observe(this.container);
    }

    // Start animation loop
    this.animate();

    const rendererStatus = this.renderer ? 'WebGL' : 'no-renderer (headless)';
    console.log(
      `[Preview] Three.js scene initialized (theme: ${this.currentTheme}, renderer: ${rendererStatus})`
    );
  }

  /**
   * Show a user-visible notice in the preview container when WebGL context
   * creation fails. Without this, the pane is silently blank (init() clears
   * the container, and the headless fallback only logs to the console).
   *
   * Note: three is pinned to ^0.162 (the last release that falls back to
   * WebGL 1) so the preview works even where WebGL 2 is disabled. This
   * notice only appears when WebGL is entirely unavailable.
   */
  _showWebGLUnavailableNotice() {
    if (this.container.querySelector('.preview-webgl-error')) return;

    const notice = document.createElement('div');
    notice.className = 'preview-webgl-error';
    notice.setAttribute('role', 'alert');

    // An h2, not an h3: in Forge the notice sits under the "Preview Settings
    // & Info" h2, but Classic does not carry those Forge panel headings, so an
    // h3 would skip a level after the h1 for a reader navigating by heading,
    // on the very notice written for people whose browser blocks WebGL. h2 is
    // correct in both: it follows the h1 in Classic and sits beside the panel
    // headings in Forge.
    const heading = document.createElement('h2');
    heading.textContent = '3D preview unavailable';

    const reason = document.createElement('p');
    reason.textContent =
      'Your browser blocked WebGL, which is required to display the 3D model. ' +
      'Rendering and exporting (STL, OBJ, etc.) still work normally.';

    const howToFix = document.createElement('p');
    howToFix.textContent = 'To enable the 3D preview:';

    const steps = document.createElement('ul');
    const stepTexts = [
      'Make sure hardware acceleration is enabled in your browser settings.',
      'In Firefox: open about:config and check that "webgl.disabled" is false.',
      'Privacy-hardening extensions or profiles may also block WebGL. Try allowing it for this site.',
    ];
    for (const text of stepTexts) {
      const li = document.createElement('li');
      li.textContent = text;
      steps.appendChild(li);
    }

    notice.append(heading, reason, howToFix, steps);
    this.container.appendChild(notice);

    announceImmediate(
      '3D preview unavailable: WebGL is disabled in this browser. Rendering and export still work.'
    );
  }

  /**
   * Detect current theme from document
   * @returns {string} 'light' | 'dark' | 'light-hc' | 'dark-hc' | 'mono' | 'mono-light' | 'mono-hc' | 'mono-light-hc'
   */
  detectTheme() {
    const root = document.documentElement;

    // Classic renders one fixed desktop appearance, so it outranks the
    // theme, high-contrast and Alt View settings (see classic.css). Which
    // appearance is the user's own choice from Preferences ▸ 3D View;
    // 'cornfield' resolves to the 'classic' palette, the historical default.
    if (document.body?.dataset.uiMode === 'classic') {
      return schemeColorsKey(this.viewportScheme ?? DEFAULT_VIEWPORT_SCHEME);
    }

    const highContrast = root.getAttribute('data-high-contrast') === 'true';
    const dataTheme = root.getAttribute('data-theme');

    // Check for variant override (takes precedence)
    const uiVariant = root.getAttribute('data-ui-variant');
    if (uiVariant === 'mono') {
      let base;
      if (dataTheme === 'light') {
        base = 'mono-light';
      } else if (dataTheme === 'dark') {
        base = 'mono';
      } else {
        base = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'mono'
          : 'mono-light';
      }
      return highContrast ? `${base}-hc` : base;
    }

    let baseTheme;
    if (dataTheme === 'dark') {
      baseTheme = 'dark';
    } else if (dataTheme === 'light') {
      baseTheme = 'light';
    } else {
      // Auto mode - check system preference
      baseTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    return highContrast ? `${baseTheme}-hc` : baseTheme;
  }

  /**
   * Update preview colors for theme change
   * @param {string} theme - 'light', 'dark', 'light-hc', or 'dark-hc'
   * @param {boolean} highContrast - High contrast mode enabled
   */
  updateTheme(theme, highContrast = false) {
    // Determine theme key. The Classic viewport schemes have no -hc siblings
    // by design: Classic renders one fixed desktop appearance and ignores
    // high contrast entirely. Suffixing one would look up a key that does not
    // exist and silently fall back to the light theme, so the user's chosen
    // scheme would vanish the moment high contrast was on.
    let themeKey = theme;
    if (highContrast && !isViewportSchemeKey(theme) && !theme.endsWith('-hc')) {
      themeKey = `${theme}-hc`;
    }

    if (!this.scene || themeKey === this.currentTheme) return;

    this.currentTheme = themeKey;
    this.highContrast = highContrast;
    const colors = PREVIEW_COLORS[themeKey] || PREVIEW_COLORS.light;

    // Update scene background
    this.scene.background.setHex(colors.background);

    // Update grid colors (custom override takes precedence over theme)
    if (this.gridHelper) {
      this._rebuildGrid();
    }

    // Keep mesh materials aligned with the current override/theme rules.
    if (this.mesh) {
      this._syncColorOverride();
    }

    // Refresh measurements if they're visible
    if (this.measurementsEnabled && this.mesh) {
      this.showMeasurements();
    }

    // Notify any theme-sensitive overlays (e.g. axis tick labels) so
    // they can rebuild themselves with the new foreground color.
    this._fireThemeChangeListeners();

    console.log(`[Preview] Theme updated to ${themeKey}`);
  }

  /**
   * Set a color override for the model material
   * @param {string|null} hexColor
   */
  setColorOverride(hexColor) {
    this.colorOverride = normalizeHexColor(hexColor);
    if (this.colorOverrideEnabled) {
      this.applyColorToMesh();
    }
  }

  /**
   * Enable or disable the color override. When disabled, COFF per-face
   * vertex colors display naturally; when enabled, the user's solid color
   * is forced onto the mesh by turning off Three.js vertex coloring.
   * @param {boolean} enabled
   */
  setColorOverrideEnabled(enabled) {
    this.colorOverrideEnabled = enabled;
    this._syncColorOverride();
  }

  /**
   * Synchronize mesh material state with the current colorOverrideEnabled
   * flag. Toggles vertex coloring on/off to switch between COFF per-face
   * colors and a solid user-chosen color without re-rendering geometry.
   */
  _syncColorOverride() {
    if (!this.mesh) return;

    const applyToMaterial = (material, geometry) => {
      if (this.colorOverrideEnabled && this.colorOverride) {
        material.vertexColors = false;
        material.color.setHex(parseInt(this.colorOverride.slice(1), 16));
        material.needsUpdate = true;
      } else {
        const hasVertexColors = geometry?.attributes?.color != null;
        if (hasVertexColors) {
          material.vertexColors = true;
          material.color.setHex(0xffffff);
          material.needsUpdate = true;
        } else {
          material.vertexColors = false;
          const themeColors =
            PREVIEW_COLORS[this.currentTheme] || PREVIEW_COLORS.light;
          material.color.setHex(themeColors.model);
          material.needsUpdate = true;
        }
      }

      if (material.userData?.backfaceColorUniform) {
        material.userData.backfaceColorUniform.value.setHex(
          this._resolveModelBackColor()
        );
      }
    };

    if (this.mesh.isGroup) {
      this.mesh.children.forEach((child) => {
        if (child.material && !child.userData.isHighlightOverlay) {
          applyToMaterial(child.material, child.geometry);
        }
      });
    } else if (this.mesh.material) {
      applyToMaterial(this.mesh.material, this.mesh.geometry);
    }
  }

  /**
   * Set the current render state. Kept for API compatibility: a no-op,
   * because model color comes from COFF per-face data or the theme default.
   * @param {'preview'|'laser'|null} _state
   */
  setRenderState(_state) {}

  /**
   * Resolve the model color to apply, respecting the priority chain:
   *   1. colorOverride (user manual pick or SCAD-derived) — only when enabled
   *   2. PREVIEW_COLORS[theme].model (theme default)
   *
   * @returns {string} 6-digit hex color string with leading '#'
   */
  _resolveModelColor() {
    if (this.colorOverrideEnabled && this.colorOverride)
      return this.colorOverride;

    const themeColors =
      PREVIEW_COLORS[this.currentTheme] || PREVIEW_COLORS.light;
    return `#${themeColors.model.toString(16).padStart(6, '0')}`;
  }

  /**
   * Resolve the back-face (CUTOUT) color for the current theme.
   * Always theme-driven — color overrides do not affect back faces,
   * matching desktop OpenSCAD where CUTOUT color is scheme-level.
   *
   * @returns {number} hex integer (e.g. 0x9dcb51)
   */
  _resolveModelBackColor() {
    const themeColors =
      PREVIEW_COLORS[this.currentTheme] || PREVIEW_COLORS.light;
    return themeColors.modelBack;
  }

  /**
   * Edge-overlay line color for the active theme. Used by the display
   * options controller so the edges overlay stays legible (≥4.5:1
   * against the model color) on every theme.
   * @returns {number} 0xRRGGBB
   */
  getThemeEdgeColor() {
    const themeColors =
      PREVIEW_COLORS[this.currentTheme] || PREVIEW_COLORS.light;
    return themeColors.edges;
  }

  /**
   * CPU-side face classifier for CSG cavity detection.
   * Sets a per-vertex `aIsInner` float attribute (1.0 = inner/cavity, 0.0 = outer).
   *
   * Pass 1: dot(faceNormal, faceCentroid) < 0.1 on centered geometry (baseline).
   * Pass 2: edge adjacency via hashed vertex positions (4 decimal places).
   * Pass 3: concave-edge correction — reclassify borderline faces (dot 0.1–0.85)
   *         adjacent to inner faces across concave edges (inter-normal dot < −0.5).
   *
   * @param {BufferGeometry} geometry - centered, with computed vertex normals
   */
  _classifyInnerFaces(geometry) {
    const posAttr = geometry.getAttribute('position');
    const normAttr = geometry.getAttribute('normal');
    if (!posAttr || !normAttr) return;
    // The arithmetic lives in mesh-extras.js, so a worker can
    // run the same function for a mesh too big to run here.
    const isInner = classifyInnerFaces(posAttr.array, normAttr.array);
    geometry.setAttribute('aIsInner', new Float32BufferAttribute(isInner, 1));
  }

  /**
   * The extras a mesh gets after it loads - the cavity tint's per-vertex flag
   * and the edge overlay's segments - where the mesh's size says they should
   * be computed.
   *
   * A small mesh is classified here at once. A big one is handed
   * to a worker with copies of its positions and normals; the mesh is shown
   * meanwhile with no tint and no edges, and both arrive together, after
   * which the post-load listeners run again so the edges overlay is built
   * from the segments the worker made (display-options-controller reads them
   * from `geometry.userData.edgeSegments`).
   *
   * @param {BufferGeometry} geometry - centered, with computed vertex normals
   * @param {{wantInner?: boolean}} [options]
   */
  _scheduleMeshExtras(geometry, { wantInner = true } = {}) {
    const posAttr = geometry.getAttribute('position');
    if (!posAttr) return;
    const triangles = Math.floor(posAttr.count / 3);
    const token = ++this._extrasToken;
    if (
      triangles <= SYNC_MESH_EXTRAS_MAX_TRIANGLES ||
      typeof Worker === 'undefined'
    ) {
      if (wantInner) {
        try {
          this._classifyInnerFaces(geometry);
        } catch (e) {
          console.warn(
            '[Preview] Face classification failed, using default coloring:',
            e
          );
        }
      }
      return;
    }
    geometry.userData.extrasPending = true;
    const normAttr = geometry.getAttribute('normal');
    const positions = posAttr.array.slice();
    const normals = wantInner && normAttr ? normAttr.array.slice() : null;
    const request = {
      id: token,
      positions,
      normals,
      wantInner: Boolean(normals),
      wantEdges: true,
      thresholdDeg: 15,
      edgeBudget: 0,
    };
    const worker = this._ensureExtrasWorker();
    if (!worker) {
      delete geometry.userData.extrasPending;
      if (wantInner) this._classifyInnerFaces(geometry);
      return;
    }
    const transfers = [positions.buffer];
    if (normals) transfers.push(normals.buffer);
    worker.postMessage(request, transfers);
  }

  _ensureExtrasWorker() {
    if (this._extrasWorker) return this._extrasWorker;
    try {
      const worker = new Worker(
        new URL('./preview-geometry-worker.js', import.meta.url),
        { type: 'module' }
      );
      worker.onmessage = (event) =>
        this._onGeometryWorkerMessage(event.data || {});
      worker.onerror = (event) => {
        console.warn('[Preview] The geometry worker stopped:', event?.message);
        this._extrasWorker = null;
        this._failPendingOFF('The geometry worker stopped');
      };
      this._extrasWorker = worker;
    } catch (e) {
      console.warn('[Preview] No geometry worker; extras run inline:', e);
      this._extrasWorker = null;
    }
    return this._extrasWorker;
  }

  /**
   * Post an OFF text to the geometry worker and wait for its answer: the
   * parsed, centered soup with its normals, tint and edges. Resolves
   * `{ stale: true }` when a newer load took the token meanwhile, so the
   * caller leaves the scene alone; rejects when the worker could not parse
   * it or stopped.
   *
   * @param {string} text
   * @returns {Promise<object>}
   */
  _prepareOFFInWorker(text) {
    const worker = this._ensureExtrasWorker();
    const id = ++this._extrasToken;
    return new Promise((resolve, reject) => {
      this._pendingOFF.set(id, { resolve, reject });
      worker.postMessage({
        id,
        type: 'off',
        text,
        wantEdges: true,
        thresholdDeg: 15,
        edgeBudget: 0,
      });
    });
  }

  /** Every answer from the geometry worker, routed by what was asked. */
  _onGeometryWorkerMessage(message) {
    if (message.type === 'off-done' || message.type === 'off-error') {
      const pending = this._pendingOFF.get(message.id);
      if (!pending) return;
      this._pendingOFF.delete(message.id);
      if (message.id !== this._extrasToken) {
        pending.resolve({ stale: true });
      } else if (message.type === 'off-error') {
        pending.reject(new Error(message.message));
      } else {
        pending.resolve(message);
      }
      return;
    }
    this._onMeshExtras(message);
  }

  /** The worker stopped: nothing waiting on it may wait forever. */
  _failPendingOFF(reason) {
    for (const pending of this._pendingOFF.values()) {
      pending.reject(new Error(reason));
    }
    this._pendingOFF.clear();
  }

  /** The worker's answer for one mesh, applied only if that mesh is still up. */
  _onMeshExtras(message) {
    if (message.id !== this._extrasToken) return;
    const geometry = this.mesh?.isGroup
      ? this.mesh.children[0]?.geometry
      : this.mesh?.geometry;
    if (!geometry || !geometry.userData.extrasPending) return;
    delete geometry.userData.extrasPending;
    if (message.type === 'error') {
      console.warn('[Preview] Mesh extras failed:', message.message);
      this._firePostLoadListeners();
      return;
    }
    if (message.isInner) {
      geometry.setAttribute(
        'aIsInner',
        new Float32BufferAttribute(new Float32Array(message.isInner), 1)
      );
    }
    if (message.edges) {
      geometry.userData.edgeSegments = new Float32Array(message.edges);
      geometry.userData.edgeTotal = message.edgeTotal;
    }
    this._firePostLoadListeners();
  }

  /**
   * Configure a MeshPhongMaterial for two-sided face coloring that
   * approximates desktop OpenSCAD's F5 back-face rendering.
   *
   * Uses gl_FrontFacing combined with the CPU-side aIsInner attribute
   * (set by _classifyInnerFaces) to identify cavity/cutout faces.
   *
   * @param {MeshPhongMaterial} material
   * @param {number} backfaceHex - e.g. 0x9dcb51
   */
  _applyBackfaceColoring(material, backfaceHex) {
    material.side = DoubleSide;

    material.onBeforeCompile = (shader) => {
      shader.uniforms.backfaceColor = {
        value: new Color(backfaceHex),
      };

      shader.vertexShader = shader.vertexShader.replace(
        'varying vec3 vViewPosition;',
        'varying vec3 vViewPosition;\nattribute float aIsInner;\nvarying float vIsInner;'
      );
      shader.vertexShader = shader.vertexShader.replace(
        'vViewPosition = - mvPosition.xyz;',
        'vViewPosition = - mvPosition.xyz;\nvIsInner = aIsInner;'
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        'uniform float opacity;',
        'uniform float opacity;\nuniform vec3 backfaceColor;\nvarying float vIsInner;'
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        'vec4 diffuseColor = vec4( diffuse, opacity );',
        'bool isBack = !gl_FrontFacing || vIsInner > 0.5;\nvec4 diffuseColor = vec4( isBack ? backfaceColor : diffuse, opacity );'
      );

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <color_fragment>',
        '#include <color_fragment>\nif ( isBack ) diffuseColor = vec4( backfaceColor, opacity );'
      );

      material.userData.backfaceColorUniform = shader.uniforms.backfaceColor;
    };

    material.customProgramCacheKey = () => 'backface-coloring-v7';
  }

  /**
   * Apply the current color (override or theme default) to the mesh.
   * Safe to call even if mesh doesn't exist yet.
   */
  applyColorToMesh() {
    if (!this.mesh) return;
    if (!this.colorOverrideEnabled) return;

    const appliedHex = this._resolveModelColor();
    const hex = parseInt(appliedHex.slice(1), 16);
    if (this.mesh.isGroup) {
      this.mesh.children.forEach((child) => {
        if (child.material && !child.userData.isHighlightOverlay) {
          child.material.vertexColors = false;
          child.material.color.setHex(hex);
          child.material.needsUpdate = true;
        }
      });
    } else if (this.mesh.material) {
      this.mesh.material.vertexColors = false;
      this.mesh.material.color.setHex(hex);
      this.mesh.material.needsUpdate = true;
    }
  }

  /**
   * Dispose geometry and material for this.mesh, handling both
   * single Mesh and Group (dual-render) cases.
   */
  _disposeMeshResources() {
    if (!this.mesh) return;
    if (this.mesh.isGroup) {
      this.mesh.children.forEach((child) => {
        child.geometry?.dispose();
        child.material?.dispose();
      });
    } else {
      this.mesh.geometry?.dispose();
      this.mesh.material?.dispose();
    }
  }

  /**
   * Return the BufferGeometry used for stats/dimensions.
   * For a Group (dual-render) returns the first child's geometry.
   */
  _getPrimaryGeometry() {
    if (!this.mesh) return null;
    if (this.mesh.isGroup) {
      return this.mesh.children[0]?.geometry ?? null;
    }
    return this.mesh.geometry ?? null;
  }

  animate() {
    this.animationId = requestAnimationFrame(() => this.animate());
    if (this.controls) this.controls.update();
    if (!this.renderer) return;
    // The drawing editor and the 2D preview hide the canvas. Without a GPU
    // each frame drawn behind them is a software render, and the first frame
    // shown again would wait for every one of them.
    if (this.renderer.domElement?.style.display === 'none') return;
    if (this._renderOverride) {
      // The HFM alternative view replaces the whole pass, triad included.
      this._renderOverride();
    } else {
      this.renderer.render(this.scene, this.getActiveCamera());
      this._renderAxisTriadPass();
    }
  }

  /**
   * The corner triad's second pass: a scissored viewport in the
   * lower-left, depth cleared so the triad draws over whatever sits
   * in its corner — the desktop renders its smallaxes with GL_ALWAYS. The
   * triad camera copies only the main camera's rotation, so pan and zoom
   * leave it untouched.
   */
  _renderAxisTriadPass() {
    const triad = this._axisTriad;
    if (!triad || !this.renderer) return;

    const size = this.renderer.getSize(_triadSizeScratch);
    const box = triad.layout(size.x, size.y);
    if (!box) return;

    triad.syncTo(this.getActiveCamera());

    const renderer = this.renderer;
    renderer.autoClear = false;
    renderer.setScissorTest(true);
    renderer.setViewport(box.x, box.y, box.size, box.size);
    renderer.setScissor(box.x, box.y, box.size, box.size);
    renderer.clearDepth();
    renderer.render(triad.scene, triad.camera);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, size.x, size.y);
    renderer.autoClear = true;
  }

  /**
   * Show, hide or recolor the corner triad. Called by the display-options
   * controller wherever the Axes overlay is applied, with the same
   * resolveAxisMarkColor() result the axis lines use for the letters.
   *
   * @param {{visible: boolean, letterColorHex?: number}} opts
   */
  setAxisTriad({ visible, letterColorHex }) {
    if (!visible) {
      this._axisTriad?.dispose();
      this._axisTriad = null;
      return;
    }
    if (this._axisTriad && this._axisTriad.letterColorHex !== letterColorHex) {
      this._axisTriad.dispose();
      this._axisTriad = null;
    }
    if (!this._axisTriad) {
      this._axisTriad = buildAxisTriadOverlay({ letterColorHex });
    }
  }

  /**
   * Setup keyboard controls for camera manipulation (WCAG 2.2 SC 2.5.7)
   * Provides non-drag alternatives for orbit/pan/zoom operations
   */
  setupKeyboardControls() {
    const rotationSpeed = 0.05;
    const panSpeed = 5;
    const zoomSpeed = 10;

    this.keyboardHandler = (event) => {
      // The drawing editor lives in this container, and every key it uses -
      // arrows between regions, Escape to leave - is a key this handler would
      // otherwise spend on the camera. A button or a table row is not an INPUT,
      // so the guard below does not cover it.
      if (this._isEditorSurfaceActive) return;

      // Ignore if focus is in an input field
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT' ||
        event.target.isContentEditable
      ) {
        return;
      }

      // Check if preview container or canvas has focus
      if (
        !this.container.contains(document.activeElement) &&
        document.activeElement !== this.renderer?.domElement
      ) {
        return;
      }

      let handled = false;

      // Rotation (arrow keys without modifiers)
      // Delegates to shared rotateHorizontal/rotateVertical which correctly
      // orbit around controls.target (not the world origin).
      if (
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey
      ) {
        switch (event.key) {
          case 'ArrowLeft':
            this.rotateHorizontal(rotationSpeed);
            handled = true;
            break;
          case 'ArrowRight':
            this.rotateHorizontal(-rotationSpeed);
            handled = true;
            break;
          case 'ArrowUp':
            this.rotateVertical(rotationSpeed);
            handled = true;
            break;
          case 'ArrowDown':
            this.rotateVertical(-rotationSpeed);
            handled = true;
            break;
        }
      }

      // Pan (Shift + arrow keys)
      if (event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
        switch (event.key) {
          case 'ArrowLeft':
            this.panCamera(-panSpeed, 0);
            handled = true;
            break;
          case 'ArrowRight':
            this.panCamera(panSpeed, 0);
            handled = true;
            break;
          case 'ArrowUp':
            this.panCamera(0, panSpeed);
            handled = true;
            break;
          case 'ArrowDown':
            this.panCamera(0, -panSpeed);
            handled = true;
            break;
        }
      }

      // Zoom (+/- keys or = key for +)
      // Delegates to shared zoomCamera() which handles both perspective and orthographic.
      if (event.key === '+' || event.key === '=' || event.key === '-') {
        const zoomAmount = (event.key === '-' ? -1 : 1) * zoomSpeed;
        this.zoomCamera(zoomAmount);
        handled = true;
      }

      if (handled) {
        event.preventDefault();
        event.stopPropagation();
        // Announce camera action to screen readers
        this.announceCameraAction(event.key, event.shiftKey);
      }
    };

    document.addEventListener('keydown', this.keyboardHandler);
  }

  /**
   * Announce camera actions to screen readers
   * Uses centralized announcer for consistent behavior
   * @param {string} action - Action description or key pressed
   * @param {boolean} shiftKey - Whether shift key was pressed
   */
  announceCameraAction(action, shiftKey = false) {
    // Delegate to centralized announcer which handles message formatting
    announceCamera(action, { shiftKey });
  }

  /**
   * Load and display STL from ArrayBuffer
   * @param {ArrayBuffer} stlData - Binary STL data
   * @param {Object} [options] - Load options
   * @param {boolean} [options.preserveCamera=false] - If true, preserve current camera position instead of auto-fitting
   * @returns {Promise<{parseMs: number}>} Parse timing info
   */
  loadSTL(stlData, options = {}) {
    this.hide2DPreview();
    this.clearBackgroundShapes();
    const { preserveCamera = false } = options;
    return new Promise((resolve, reject) => {
      try {
        const parseStartTime = performance.now();

        console.log(
          '[Preview] Loading STL, size:',
          stlData.byteLength,
          'bytes'
        );

        this.hideLODWarning();

        if (this.mesh) {
          this.scene.remove(this.mesh);
          this._disposeMeshResources();
          this.mesh = null;
        }

        const loader = new STLLoader();
        const geometry = loader.parse(stlData);

        const vertexCount = geometry.attributes.position
          ? geometry.attributes.position.count
          : 0;
        const triangleCount = vertexCount / 3;
        const parseMs = Math.round(performance.now() - parseStartTime);
        console.log(
          `[Preview] STL parsed in ${parseMs}ms, vertices:`,
          vertexCount,
          'triangles:',
          triangleCount
        );

        if (vertexCount === 0) {
          console.warn('[Preview] Empty STL — no geometry produced');
          this.clear();
          resolve({ parseMs, empty: true });
          return;
        }

        // Store vertex count for LOD info
        this.lastVertexCount = vertexCount;
        this.lastTriangleCount = triangleCount;

        // Check for large model and show warning
        if (
          LOD_CONFIG.showWarning &&
          vertexCount > LOD_CONFIG.vertexWarningThreshold
        ) {
          const isCritical = vertexCount > LOD_CONFIG.vertexCriticalThreshold;
          this.showLODWarning(vertexCount, triangleCount, isCritical);
        }

        // Compute normals and center geometry
        geometry.computeVertexNormals();
        geometry.center();

        // Apply auto-bed if enabled (place object on Z=0 build plate)
        if (this.autoBedEnabled) {
          this.applyAutoBed(geometry);
        }

        // Inline for a small mesh, in a worker for a big one. After the bed,
        // so the positions the worker copies are the ones the mesh is drawn
        // with and its edges land where the model is.
        this._scheduleMeshExtras(geometry);

        // Create material using render-state-aware color resolution.
        // polygonOffset pushes the shaded surface fractionally back in
        // depth so the edges overlay (a mesh child) is not chewed up by
        // z-fighting with the facets it traces.
        const material = new MeshPhongMaterial({
          color: parseInt(this._resolveModelColor().slice(1), 16),
          specular: 0x000000,
          shininess: DESKTOP_SHININESS,
          flatShading: false,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
        });
        this._applyBackfaceColoring(material, this._resolveModelBackColor());

        this.mesh = new Mesh(geometry, material);
        this.scene.add(this.mesh);

        if (this.colorOverrideEnabled && this.colorOverride) {
          this.applyColorToMesh();
        }

        // Reset rotation centering state (new mesh needs fresh application)
        this.rotationCenteringEnabled = false;

        this._fitAfterLoad(preserveCamera);
        this._followModelCenter(preserveCamera);

        // Call post-load hook (e.g., for re-applying rotation centering)
        if (this._postLoadHook) {
          this._postLoadHook();
        }
        this._firePostLoadListeners();
        // "Top of the model" is a promise about this model, so it is
        // re-resolved whenever one arrives. Any other preset is a constant and
        // this returns immediately.
        this.refreshOverlayZ();

        if (this.measurementsEnabled) {
          this.showMeasurements();
        }

        // Update screen reader model summary (WCAG 2.2)
        this.updateModelSummary();

        console.log('[Preview] STL loaded and displayed');
        resolve({ parseMs });
      } catch (error) {
        console.error('[Preview] Failed to load STL:', error);
        reject(error);
      }
    });
  }

  /**
   * Load and display a model from OFF or COFF (Color OFF) text data.
   *
   * OFF format:
   *   OFF
   *   numVertices numFaces 0
   *   x y z          ← one line per vertex
   *   3 v0 v1 v2     ← per face: vertex count + indices
   *
   * COFF (Color OFF) — OpenSCAD color() passthrough:
   *   COFF
   *   numVertices numFaces 0
   *   x y z
   *   3 v0 v1 v2 r g b a   ← r/g/b/a are floats 0–1
   *
   * If no color data is present (plain OFF), the preview falls back to the
   * standard theme/override color exactly like loadSTL().
   *
   * @param {ArrayBuffer|string} offData - OFF/COFF file content (text or ArrayBuffer)
   * @param {Object} [options]
   * @param {boolean} [options.preserveCamera=false]
   * @param {Object|null} [options.debugHighlight=null] Desktop `#` modifier override.
   *   When set, COFF face colors are replaced with the fixed highlight color
   *   to match desktop OpenSCAD behavior: `{255,81,81,128}` overrides `color()`.
   * @param {string} options.debugHighlight.hex  Highlight hex color (#RRGGBB)
   * @param {number} options.debugHighlight.opacity Highlight opacity (0–1)
   * @returns {Promise<{parseMs: number, hasColors: boolean}>}
   */
  async loadOFF(offData, options = {}) {
    this.hide2DPreview();
    this.clearBackgroundShapes();
    const { preserveCamera = false, debugHighlight = null } = options;
    const parseStartTime = performance.now();
    try {
      const text =
        typeof offData === 'string'
          ? offData
          : new TextDecoder().decode(offData);

      // A big OFF is parsed, centered and given its normals, tint and
      // edges in the worker, and comes back as arrays the page only has to
      // wrap; a small one is done inline. The header alone decides,
      // read without splitting the text.
      const faces = offFaceCount(text);
      let parsed = null;
      if (
        faces > SYNC_MESH_EXTRAS_MAX_TRIANGLES &&
        typeof Worker !== 'undefined' &&
        this._ensureExtrasWorker()
      ) {
        try {
          parsed = await this._prepareOFFInWorker(text);
        } catch (workerError) {
          console.warn(
            '[Preview] The geometry worker could not take the OFF; parsing inline:',
            workerError
          );
          parsed = null;
        }
        if (parsed && parsed.stale) {
          return {
            parseMs: Math.round(performance.now() - parseStartTime),
            hasColors: false,
            stale: true,
          };
        }
      }
      if (!parsed) {
        parsed = parseOFF(text);
      }
      const { positions, colors, hasColors, numVerts, numFaces } = parsed;
      console.log(
        `[Preview] Loading ${parsed.isCOFF ? 'COFF' : 'OFF'} — ${numVerts} verts, ${numFaces} faces`
      );

      if (positions.length === 0) {
        console.warn('[Preview] OFF has no triangulated geometry');
        this.clear();
        return {
          parseMs: Math.round(performance.now() - parseStartTime),
          hasColors: false,
        };
      }

      const geometry = new BufferGeometry();
      geometry.setAttribute(
        'position',
        new Float32BufferAttribute(positions, 3)
      );
      if (hasColors && colors && colors.length === positions.length) {
        geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
      }
      let placement = parsed.offset || [0, 0, 0];
      if (parsed.normals) {
        // The worker centered the soup, computed the flat normals three.js
        // would have, and classified and edged it; the token it answered
        // under is the current one, so an older extras answer is dropped.
        geometry.setAttribute(
          'normal',
          new Float32BufferAttribute(parsed.normals, 3)
        );
        if (parsed.bounds) {
          geometry.boundingBox = new Box3(
            new Vector3(...parsed.bounds.min),
            new Vector3(...parsed.bounds.max)
          );
        } else {
          geometry.computeBoundingBox();
        }
        if (parsed.isInner) {
          geometry.setAttribute(
            'aIsInner',
            new Float32BufferAttribute(parsed.isInner, 1)
          );
        }
        if (parsed.edges) {
          geometry.userData.edgeSegments = parsed.edges;
          geometry.userData.edgeTotal = parsed.edgeTotal;
        }
      } else {
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        const center = geometry.boundingBox.getCenter(new Vector3());
        geometry.center();
        placement = [-center.x, -center.y, -center.z];
      }

      // The bed before the extras are scheduled, so a copy the worker
      // takes is bedded; the worker branch's segments above were made on the
      // centered soup and applyAutoBed moves them with the positions.
      if (this.autoBedEnabled) {
        this.applyAutoBed(geometry);
      }
      // Where the model's own coordinates went, so its `%` shapes can follow.
      this.modelPlacement = [
        placement[0],
        placement[1],
        placement[2] + (this.autoBedEnabled ? this.autoBedOffset || 0 : 0),
      ];

      if (!parsed.normals) {
        // Inline for a small mesh, in a worker for a big one; a mesh with its
        // own face colors gets no tint, only the edges.
        this._scheduleMeshExtras(geometry, { wantInner: !hasColors });
      }

      if (this.mesh) {
        this.scene.remove(this.mesh);
        this._disposeMeshResources();
        this.mesh = null;
      }

      if (debugHighlight) {
        // Dual-render: normal mesh + semi-transparent highlight overlay.
        // Desktop OpenSCAD F5 renders #-marked geometry at full color with
        // a pink {255,81,81,128} overlay on top.
        const normalMaterial = hasColors
          ? new MeshPhongMaterial({
              vertexColors: true,
              specular: 0x000000,
              shininess: DESKTOP_SHININESS,
              flatShading: false,
              polygonOffset: true,
              polygonOffsetFactor: 1,
              polygonOffsetUnits: 1,
            })
          : new MeshPhongMaterial({
              color: parseInt(this._resolveModelColor().slice(1), 16),
              specular: 0x000000,
              shininess: DESKTOP_SHININESS,
              flatShading: false,
              polygonOffset: true,
              polygonOffsetFactor: 1,
              polygonOffsetUnits: 1,
            });
        this._applyBackfaceColoring(
          normalMaterial,
          this._resolveModelBackColor()
        );

        const highlightGeometry = geometry.clone();
        const highlightMaterial = new MeshPhongMaterial({
          color: parseInt(debugHighlight.hex.replace('#', ''), 16),
          specular: 0x000000,
          shininess: DESKTOP_SHININESS,
          flatShading: false,
          transparent: true,
          opacity: debugHighlight.opacity,
          depthWrite: false,
        });

        const normalMesh = new Mesh(geometry, normalMaterial);
        const highlightMesh = new Mesh(highlightGeometry, highlightMaterial);
        highlightMesh.userData.isHighlightOverlay = true;
        highlightMesh.renderOrder = 1;

        this.mesh = new Group();
        this.mesh.add(normalMesh);
        this.mesh.add(highlightMesh);
      } else {
        const useVertexColors =
          hasColors && !(this.colorOverrideEnabled && this.colorOverride);
        const material = useVertexColors
          ? new MeshPhongMaterial({
              vertexColors: true,
              specular: 0x000000,
              shininess: DESKTOP_SHININESS,
              flatShading: false,
              polygonOffset: true,
              polygonOffsetFactor: 1,
              polygonOffsetUnits: 1,
            })
          : new MeshPhongMaterial({
              color: parseInt(this._resolveModelColor().slice(1), 16),
              specular: 0x000000,
              shininess: DESKTOP_SHININESS,
              flatShading: false,
              polygonOffset: true,
              polygonOffsetFactor: 1,
              polygonOffsetUnits: 1,
            });
        this._applyBackfaceColoring(material, this._resolveModelBackColor());
        this.mesh = new Mesh(geometry, material);
      }
      this.scene.add(this.mesh);

      const vertexCount = positions.length / 3;
      const triangleCount = vertexCount / 3;
      this.lastVertexCount = vertexCount;
      this.lastTriangleCount = triangleCount;

      if (!hasColors && this.colorOverrideEnabled && this.colorOverride) {
        this.applyColorToMesh();
      }

      this.rotationCenteringEnabled = false;
      this._fitAfterLoad(preserveCamera);
      this._followModelCenter(preserveCamera);
      if (this._postLoadHook) {
        this._postLoadHook();
      }
      this._firePostLoadListeners();
      // "Top of the model" is a promise about this model, so it is
      // re-resolved whenever one arrives. Any other preset is a constant and
      // this returns immediately.
      this.refreshOverlayZ();
      if (this.measurementsEnabled) {
        this.showMeasurements();
      }
      this.updateModelSummary();

      const parseMs = Math.round(performance.now() - parseStartTime);
      console.log(
        `[Preview] OFF loaded in ${parseMs}ms — ${triangleCount} triangles, hasColors=${hasColors}`
      );
      return { parseMs, hasColors };
    } catch (error) {
      console.error('[Preview] Failed to load OFF:', error);
      throw error;
    }
  }

  /**
   * Draw the model's `%` shapes see-through, as desktop OpenSCAD's preview
   * does: each face in its own color and alpha, gray where it has none,
   * moved as the model was. Call after loadOFF for the same model.
   * @param {ArrayBuffer|string} offData - The OFF of the `%` shapes alone
   * @returns {{triangles: number}}
   */
  loadBackgroundShapesOFF(offData) {
    this.clearBackgroundShapes();
    const text =
      typeof offData === 'string' ? offData : new TextDecoder().decode(offData);
    const { positions, colors } = parseOFFWithAlpha(text);
    if (positions.length === 0) return { triangles: 0 };
    const [dx, dy, dz] = this.modelPlacement || [0, 0, 0];
    for (let i = 0; i < positions.length; i += 3) {
      positions[i] += dx;
      positions[i + 1] += dy;
      positions[i + 2] += dz;
    }
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 4));
    geometry.computeVertexNormals();
    const material = new MeshPhongMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
      specular: 0x000000,
      shininess: DESKTOP_SHININESS,
    });
    this.backgroundShapes = new Mesh(geometry, material);
    this.backgroundShapes.userData.isBackgroundShapes = true;
    this.backgroundShapes.renderOrder = 2;
    this.scene.add(this.backgroundShapes);
    return { triangles: positions.length / 9 };
  }

  /** Remove the `%` shapes drawn for the previous model, if any. */
  clearBackgroundShapes() {
    if (!this.backgroundShapes) return;
    this.scene.remove(this.backgroundShapes);
    this.backgroundShapes.geometry.dispose();
    this.backgroundShapes.material.dispose();
    this.backgroundShapes = null;
  }

  /**
   * Show LOD (Level of Detail) warning for large models
   * @param {number} vertexCount - Number of vertices
   * @param {number} triangleCount - Number of triangles
   * @param {boolean} isCritical - Whether the model is critically large
   */
  showLODWarning(vertexCount, triangleCount, isCritical = false) {
    if (this.isLODWarningPermanentlyDismissed()) return;

    this.hideLODWarning();

    const warningLevel = isCritical ? 'critical' : 'warning';
    const warningTitle = isCritical
      ? 'Very Large Model Detected'
      : 'Large Model Detected';
    const warningMessage = isCritical
      ? 'This model may cause your browser to become unresponsive.'
      : 'Preview performance may be affected on some devices.';

    const warningDiv = document.createElement('div');
    warningDiv.className = `lod-warning lod-warning--${warningLevel}`;
    warningDiv.id = 'lodWarning';
    warningDiv.setAttribute('role', 'alert');
    warningDiv.setAttribute('aria-live', 'polite');

    warningDiv.innerHTML = `
      <div class="lod-warning-header">
        <span class="lod-warning-icon" aria-hidden="true">${isCritical ? '🔴' : '⚠️'}</span>
        <strong class="lod-warning-title">${warningTitle}</strong>
      </div>
      <div class="lod-warning-content">
        <p class="lod-warning-message">${warningMessage}</p>
        <p class="lod-warning-stats">
          <strong>${vertexCount.toLocaleString()}</strong> vertices
          · <strong>${triangleCount.toLocaleString()}</strong> triangles
        </p>
      </div>
      <div class="lod-warning-actions">
        <button type="button" class="btn btn-sm btn-ghost" id="lodWarningDismissPermanent" aria-label="Don't show this warning again">
          Don't show again
        </button>
        <button type="button" class="btn btn-sm btn-outline" id="lodWarningDismiss" aria-label="Dismiss warning">
          Got it
        </button>
      </div>
    `;

    this.container.appendChild(warningDiv);

    warningDiv
      .querySelector('#lodWarningDismiss')
      ?.addEventListener('click', () => {
        this.hideLODWarning();
      });

    warningDiv
      .querySelector('#lodWarningDismissPermanent')
      ?.addEventListener('click', () => {
        this.dismissLODWarningPermanently();
      });

    console.log(
      `[Preview] LOD warning shown: ${vertexCount} vertices (${warningLevel})`
    );
  }

  /**
   * Hide the LOD warning if visible
   */
  hideLODWarning() {
    const existingWarning = this.container?.querySelector('#lodWarning');
    if (existingWarning) {
      existingWarning.remove();
    }
  }

  /**
   * Permanently dismiss the LOD warning so it never reappears across renders.
   * The preference is stored in localStorage and survives page reloads.
   */
  dismissLODWarningPermanently() {
    safeSetItem(STORAGE_KEY_LOD_WARNING_DISMISSED, 'true');
    this.hideLODWarning();
    console.log('[Preview] LOD warning permanently dismissed by user');
  }

  /**
   * @returns {boolean} Whether the user has permanently dismissed LOD warnings.
   */
  isLODWarningPermanentlyDismissed() {
    return safeGetItem(STORAGE_KEY_LOD_WARNING_DISMISSED) === 'true';
  }

  /**
   * Re-enable LOD warnings after a previous permanent dismiss.
   */
  resetLODWarningDismissal() {
    safeRemoveItem(STORAGE_KEY_LOD_WARNING_DISMISSED);
    console.log('[Preview] LOD warning dismissal reset');
  }

  /**
   * Get current LOD statistics
   * @returns {Object} LOD stats { vertexCount, triangleCount, isLarge, isCritical }
   */
  getLODStats() {
    const vertexCount = this.lastVertexCount || 0;
    const triangleCount = this.lastTriangleCount || 0;

    return {
      vertexCount,
      triangleCount,
      isLarge: vertexCount > LOD_CONFIG.vertexWarningThreshold,
      isCritical: vertexCount > LOD_CONFIG.vertexCriticalThreshold,
    };
  }

  /**
   * Show a color legend overlay when the SCAD model defines multiple color
   * parameters. Gives the user visual confirmation of their color choices even
   * though the 3D mesh is rendered in a single color (STL limitation).
   *
   * @param {Array<{name: string, value: string}>} colorParams - Color
   *   parameter entries, each with a human-readable name and hex value.
   */
  showColorLegend(colorParams) {
    this.hideColorLegend();
    if (
      !this.container ||
      !Array.isArray(colorParams) ||
      colorParams.length === 0
    )
      return;

    const panel = document.createElement('div');
    panel.id = 'colorLegend';
    panel.className = 'color-legend';
    panel.setAttribute('role', 'status');
    panel.setAttribute('aria-label', 'Model color settings');

    const heading = document.createElement('span');
    heading.className = 'color-legend-title';
    heading.textContent = 'Color Settings';
    panel.appendChild(heading);

    for (const { name, value } of colorParams) {
      const row = document.createElement('div');
      row.className = 'color-legend-row';

      const swatch = document.createElement('span');
      swatch.className = 'color-legend-swatch';
      swatch.style.backgroundColor = value || '#888';
      swatch.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'color-legend-label';
      const displayName = name.replace(/_/g, ' ');
      label.textContent = `${displayName}: ${value || '(none)'}`;

      row.appendChild(swatch);
      row.appendChild(label);
      panel.appendChild(row);
    }

    this.container.appendChild(panel);
  }

  /**
   * Remove the color legend overlay.
   */
  hideColorLegend() {
    const existing = this.container?.querySelector('#colorLegend');
    if (existing) existing.remove();
  }

  /**
   * Fit camera to model bounds (Z-up coordinate system, OpenSCAD-style diagonal view)
   *
   * Matches OpenSCAD desktop's default $vpr = [55, 0, 25]:
   *   azimuth  = 25° from front (-Y) toward right (+X)
   *   elevation = 35° above the XY plane
   *
   * Works correctly in both perspective and orthographic projection modes.
   * Also re-asserts Z-up, so a camera that somehow lost it comes back sane.
   */
  /**
   * A pose typed into the Viewport-Control panel holds the camera: a render
   * that lands after it (the first preview, slow on a software renderer)
   * keeps the held pose instead of fitting the camera over it. A new file
   * releases the hold.
   */
  holdCamera() {
    this._cameraHeld = true;
  }

  releaseCamera() {
    this._cameraHeld = false;
  }

  // The fit a loaded model gets, unless the camera is to be kept or is held.
  _fitAfterLoad(preserveCamera) {
    if (preserveCamera || this._cameraHeld) return;
    this.fitCameraToModel();
  }

  fitCameraToModel() {
    if (!this.mesh) return;

    // Compute bounding box
    const box = new Box3().setFromObject(this.mesh);
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraDistance *= 1.8; // Padding

    // OpenSCAD default diagonal: $vpr = [55, 0, 25]
    // azimuth 25° from front, elevation 35° above XY plane
    const azimuth = 25 * (Math.PI / 180); // 25° - OpenSCAD default
    const elevation = 35 * (Math.PI / 180); // 35° (= 90° - 55°) above XY plane

    const horizontalDist = cameraDistance * Math.cos(elevation);
    const verticalDist = cameraDistance * Math.sin(elevation);

    const camera = this.getActiveCamera();

    camera.up.set(...PreviewManager.WORLD_UP);

    camera.position.set(
      center.x + horizontalDist * Math.sin(azimuth), // X: slightly right
      center.y - horizontalDist * Math.cos(azimuth), // Y: mostly front (negative Y)
      center.z + verticalDist // Z: above
    );
    camera.lookAt(center);

    // Update controls target
    if (this.controls) this.controls.target.copy(center);

    // Update orthographic frustum to fit the model
    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      const aspect = this.container.clientWidth / this.container.clientHeight;
      const frustumHeight = maxDim * 1.8;
      this.orthoCamera.left = (frustumHeight * aspect) / -2;
      this.orthoCamera.right = (frustumHeight * aspect) / 2;
      this.orthoCamera.top = frustumHeight / 2;
      this.orthoCamera.bottom = frustumHeight / -2;
      this.orthoCamera.zoom = 1;
      this.orthoCamera.updateProjectionMatrix();
    }

    if (this.controls) this.controls.update();

    // Store initial aspect for resize tracking
    this._lastAspect = this.camera.aspect;

    console.log(
      '[Preview] Camera fitted to model (Z-up), size:',
      size,
      'distance:',
      cameraDistance
    );
  }

  /**
   * Set the orthographic frustum from a visible height, keeping the container's
   * aspect ratio and clearing any accumulated zoom.
   *
   * @param {number} frustumHeight - World-space height the frustum should show
   * @private
   */
  _applyOrthoFrustum(frustumHeight) {
    if (!this.orthoCamera) return;

    const width = this.container?.clientWidth || 0;
    const height = this.container?.clientHeight || 0;
    const aspect = height > 0 ? width / height : this.camera?.aspect || 1;

    this.orthoCamera.left = (frustumHeight * aspect) / -2;
    this.orthoCamera.right = (frustumHeight * aspect) / 2;
    this.orthoCamera.top = frustumHeight / 2;
    this.orthoCamera.bottom = frustumHeight / -2;
    this.orthoCamera.zoom = 1;
    this.orthoCamera.updateProjectionMatrix();
  }

  /**
   * Restore the startup camera pose, whatever is or is not loaded.
   *
   * This is View ▸ Reset View. It ignores the model on purpose: upstream's
   * Reset View returns the camera to its default angle, distance and target,
   * which is what separates it from View All (fit the model, keep the angle)
   * and Center (keep the angle and distance, re-centre on the model). Called
   * from the View menu, the `resetView` shortcut and the camera bar.
   */
  resetCamera() {
    if (!this.camera) return;

    const camera = this.getActiveCamera();
    const [x, y, z] = PreviewManager.DEFAULT_CAMERA_POSITION;

    camera.up.set(...PreviewManager.WORLD_UP);
    camera.position.set(x, y, z);
    camera.lookAt(0, 0, 0);

    if (this.controls) this.controls.target.set(0, 0, 0);

    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      // The frustum-from-distance relationship toggleProjection() keeps, so a
      // reset shows the same area in orthographic as it does in perspective.
      const distance = Math.sqrt(x * x + y * y + z * z);
      const fovRad = this.camera.fov * (Math.PI / 180);
      this._applyOrthoFrustum(2 * distance * Math.tan(fovRad / 2));
    }

    if (this.controls) this.controls.update();

    console.log('[Preview] Camera reset to default pose');
  }

  /**
   * Re-centre the view on the model without changing the angle or distance.
   *
   * This is View ▸ Center: the desktop clears the pan offset, so the object
   * returns to the middle of the viewport and nothing else moves.
   *
   * @returns {boolean} true when the view moved
   */
  centerCamera() {
    if (!this.camera || !this.mesh) return false;

    const center = new Box3().setFromObject(this.mesh).getCenter(new Vector3());
    const camera = this.getActiveCamera();
    const target = this.controls ? this.controls.target : new Vector3();

    camera.position.add(center.clone().sub(target));
    if (this.controls) this.controls.target.copy(center);
    camera.lookAt(center);

    if (this.controls) this.controls.update();

    return true;
  }

  /**
   * Keep the view on the model when a new one arrives with the camera kept
   * (a parameter change): the camera and the orbit point move by as much as
   * the model's center moved. A view centered on the model stays centered,
   * so turning it still turns about the model; a pan the person made keeps
   * its place on the model; the angle and the distance do not change. A
   * load that fits the camera only records the center.
   *
   * @param {boolean} moveView - true when the camera was kept
   */
  _followModelCenter(moveView) {
    if (!this.mesh) return;
    const center = new Box3().setFromObject(this.mesh).getCenter(new Vector3());
    const previous = this._modelCenter;
    this._modelCenter = center;
    if (!moveView || !previous || !this.controls) return;
    const shift = center.clone().sub(previous);
    if (shift.lengthSq() === 0) return;
    this.getActiveCamera().position.add(shift);
    this.controls.target.add(shift);
    this.controls.update();
  }

  /**
   * OrbitControls clamps the polar angle at the poles, so from Top only an
   * upward drag would tilt away (the downward one presses a dead clamp),
   * where the desktop rolls straight over. This carries the camera over the
   * pole when a gesture keeps pushing against the clamp: azimuth flips half a
   * turn and the camera lands a real tilt past the pole, exactly the far side
   * of the roll-over. One crossing per gesture, so a single pull cannot
   * ping-pong across.
   *
   * The WORLD_UP invariant holds by construction: pure vector geometry on
   * camera.position; camera.up is never read or written.
   */
  _setupPoleCrossing() {
    const el = this.renderer?.domElement;
    if (!el) return;
    this._poleGesture = { active: false, lastY: 0, acc: 0, crossed: false };

    el.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      this._poleGesture.active = true;
      this._poleGesture.lastY = e.clientY;
      this._poleGesture.acc = 0;
      this._poleGesture.crossed = false;
      this._poleGesture.pointerId = e.pointerId;
    });
    window.addEventListener('pointerup', () => {
      this._poleGesture.active = false;
      this._poleGesture.acc = 0;
    });
    el.addEventListener('pointermove', (e) => {
      const g = this._poleGesture;
      if (!g.active || g.crossed || !this.controls?.enabled) return;
      const dy = e.clientY - g.lastY;
      g.lastY = e.clientY;

      const polar = this.controls.getPolarAngle();
      const NEAR = 0.02; // ~1.1 degrees - inside this, the clamp is what stops you
      const atTop = polar < NEAR;
      const atBottom = polar > Math.PI - NEAR;
      // At Top the downward drag (dy > 0) presses the clamp; at Bottom the
      // upward one. Any other motion resets the intent.
      if (atTop && dy > 0) g.acc += dy;
      else if (atBottom && dy < 0) g.acc += dy;
      else g.acc = 0;

      const THRESHOLD_PX = 14;
      let side = null;
      if (g.acc > THRESHOLD_PX) side = 'top';
      else if (g.acc < -THRESHOLD_PX) side = 'bottom';
      if (side) {
        g.acc = 0;
        g.crossed = this._crossPole(side);
        if (g.crossed) {
          // The rest of this gesture still belongs to OrbitControls, and it
          // would rotate straight back into the pole from the far side
          // (measured: the crossing fired, then eight remaining moves undid
          // it). End the library's drag at the moment of crossing; the next
          // grab starts on the far side.
          g.active = false;
          el.dispatchEvent(
            new PointerEvent('pointercancel', {
              pointerId: g.pointerId,
              bubbles: true,
            })
          );
        }
      }
    });
  }

  /**
   * Carry the camera over the given pole: same distance, azimuth + half a
   * turn, landing CROSS_LANDING off the pole on the far side.
   * @param {'top'|'bottom'} side
   * @returns {boolean} true when the camera moved
   */
  _crossPole(side) {
    if (!this.controls) return false;
    const camera = this.getActiveCamera();
    const target = this.controls.target;

    // Damping keeps a residue of the gesture's rotation and would decay it
    // against the landing (measured: ~7 degrees clawed back toward the
    // pole). One undamped update consumes the pending delta into the clamp,
    // where it can do nothing, so the landing below is exact.
    const wasDamping = this.controls.enableDamping;
    this.controls.enableDamping = false;
    this.controls.update();
    this.controls.enableDamping = wasDamping;
    const offset = camera.position.clone().sub(target);
    const dist = offset.length();
    if (dist < 1e-6) return false;

    // The horizontal heading we arrived on; at the pole it is tiny but real
    // (the face views sit POLE_EPSILON off the pole, aimed a hair toward -Y).
    const h = new Vector3(offset.x, offset.y, 0);
    if (h.lengthSq() < 1e-12) h.set(0, -1, 0);
    h.normalize().negate(); // azimuth + PI: out the other side

    const CROSS_LANDING = (12 * Math.PI) / 180; // a real, visible tilt past
    const sin = Math.sin(CROSS_LANDING);
    const cos =
      side === 'top' ? Math.cos(CROSS_LANDING) : -Math.cos(CROSS_LANDING);

    camera.position
      .copy(target)
      .addScaledVector(h, dist * sin)
      .add(new Vector3(0, 0, dist * cos));
    camera.lookAt(target);
    this.controls.update();
    return true;
  }

  /**
   * Fit the whole model in view without changing the viewing angle.
   *
   * This is View ▸ View All. `fitCameraToModel()` also snaps back to the
   * default diagonal, which is what a freshly loaded model wants but would
   * make View All and Reset View the same command.
   *
   * @returns {boolean} true when the view moved
   */
  viewAllCamera() {
    if (!this.camera || !this.mesh) return false;

    const box = new Box3().setFromObject(this.mesh);
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    const camera = this.getActiveCamera();
    const target = this.controls ? this.controls.target : new Vector3();

    // Keep the direction the user is already looking from; fall back to the
    // default diagonal when the camera sits exactly on its own target.
    const direction = camera.position.clone().sub(target);
    if (direction.lengthSq() < 1e-6) {
      direction.set(...PreviewManager.CAMERA_VIEWS.diagonal.direction);
    }
    direction.normalize();

    const fovRad = this.camera.fov * (Math.PI / 180);
    const distance = Math.abs(maxDim / 2 / Math.tan(fovRad / 2)) * 1.8; // Padding

    camera.position.copy(center).addScaledVector(direction, distance);
    camera.lookAt(center);
    if (this.controls) this.controls.target.copy(center);

    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      this._applyOrthoFrustum(maxDim * 1.8);
    }

    if (this.controls) this.controls.update();

    return true;
  }

  /**
   * The pose the camera starts in — OpenSCAD's diagonal view of the origin.
   * Defined once so `init()` and `resetCamera()` cannot drift apart.
   */
  static DEFAULT_CAMERA_POSITION = [150, -150, 100];

  /**
   * The world's up axis. OpenSCAD is Z-up, and this app has exactly one up:
   * OrbitControls captures its orbit frame from `camera.up` once, inside the
   * IIFE that builds `update()`, so a later write never reaches the orbit
   * maths, but the `lookAt(target)` at the end of every frame's update does
   * read it. Leaving a different up behind therefore cannot re-aim the
   * turntable; it only rolls the picture, and the roll grows with every drag
   * until the view is un-navigable. Nothing may set a different one.
   */
  static WORLD_UP = [0, 0, 1];

  /**
   * How far off the pole the Top and Bottom views sit.
   *
   * `lookAt` cannot build a camera frame when the view direction is parallel
   * to `up` — three falls back to nudging the direction by 0.0001, which lands
   * Top with +Y across the screen instead of up it — and OrbitControls clamps
   * the polar angle of a camera sitting exactly on a pole. Both views are
   * therefore aimed a hair toward -Y, which is also what puts +Y up the screen
   * for Top and down it for Bottom, matching the desktop.
   *
   * The size is measured, not guessed. Looking straight down, the Z axis and
   * its scale marks lie in a plane that contains the view direction, so they
   * collapse to a line — and the ones nearest the camera blow up under
   * perspective the instant that plane opens even slightly. Comparing renders
   * of the Top and Bottom views against the exactly-on-pole ones, the strongly
   * changed pixels run 2890 at 1e-3, 948 at 1e-4, 489 at 3e-5 and 7 at 1e-5,
   * where what is left is one antialiased axis line landing on a different
   * row. 1e-5 is 0.00057°, and still ten times OrbitControls' polar epsilon.
   */
  static POLE_EPSILON = 0.00001;

  /**
   * Standard camera views for OpenSCAD-style viewing (Z-up coordinate system)
   *
   * direction: unit-ish vector from the model center toward the camera.
   *
   * There is deliberately no per-view up: screen orientation follows from the
   * direction and WORLD_UP alone, which is what keeps the orbit a turntable
   * around global Z after every one of these.
   *
   * The diagonal view matches OpenSCAD's default $vpr = [55, 0, 25]:
   *   azimuth 25° from front (-Y) toward right (+X), elevation 35° above XY plane.
   *   direction ≈ [sin(25°)cos(35°), -cos(25°)cos(35°), sin(35°)]
   *            ≈ [0.346, -0.742, 0.574]
   */
  static CAMERA_VIEWS = {
    top: { name: 'Top', direction: [0, -PreviewManager.POLE_EPSILON, 1] },
    bottom: {
      name: 'Bottom',
      direction: [0, -PreviewManager.POLE_EPSILON, -1],
    },
    front: { name: 'Front', direction: [0, -1, 0] },
    back: { name: 'Back', direction: [0, 1, 0] },
    left: { name: 'Left', direction: [-1, 0, 0] },
    right: { name: 'Right', direction: [1, 0, 0] },
    diagonal: { name: 'Diagonal', direction: [0.346, -0.742, 0.574] },
  };

  /**
   * Set camera to a standard view angle.
   * Works correctly in both perspective and orthographic projection modes.
   * @param {string} viewName - Name of the view (top, bottom, front, back, left, right, diagonal)
   */
  setCameraView(viewName) {
    const view = PreviewManager.CAMERA_VIEWS[viewName];
    if (!view) {
      console.warn(`[Preview] Unknown camera view: ${viewName}`);
      return;
    }

    if (!this.mesh) {
      console.warn('[Preview] No mesh loaded, cannot set camera view');
      announceImmediate('Load a model first to use camera views');
      return;
    }

    // Compute bounding box to get center and appropriate distance
    const box = new Box3().setFromObject(this.mesh);
    const center = box.getCenter(new Vector3());
    const size = box.getSize(new Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    // Always use the perspective camera FOV for distance calculation
    const fov = this.camera.fov * (Math.PI / 180);
    let cameraDistance = Math.abs(maxDim / 2 / Math.tan(fov / 2));
    cameraDistance *= 1.8; // Padding

    // Use whichever camera is currently active
    const camera = this.getActiveCamera();
    const direction = new Vector3(...view.direction).normalize();

    // Position camera along the direction vector from center
    camera.position.copy(center).addScaledVector(direction, cameraDistance);

    // Z-up, always — see WORLD_UP. The pose itself carries the screen
    // orientation, so nothing here needs its own up.
    camera.up.set(...PreviewManager.WORLD_UP);

    // Look at center
    camera.lookAt(center);

    // Update controls target
    if (this.controls) this.controls.target.copy(center);

    // Update orthographic frustum to fit the model at the new view
    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      const aspect = this.container.clientWidth / this.container.clientHeight;
      const frustumHeight = maxDim * 1.8;
      this.orthoCamera.left = (frustumHeight * aspect) / -2;
      this.orthoCamera.right = (frustumHeight * aspect) / 2;
      this.orthoCamera.top = frustumHeight / 2;
      this.orthoCamera.bottom = frustumHeight / -2;
      this.orthoCamera.zoom = 1;
      this.orthoCamera.updateProjectionMatrix();
    }

    if (this.controls) this.controls.update();

    // Announce to screen readers
    this.announceCameraAction(`${view.name} view`);

    console.log(`[Preview] Camera set to ${view.name} view`);
  }

  /**
   * Toggle between perspective and orthographic projection.
   *
   * The key insight: the orthographic frustum must match the visible area that
   * the perspective camera sees at the controls.target distance. This is:
   *   frustumHeight = 2 * distance * tan(fov / 2)
   *
   * @returns {string} The new projection mode ('perspective' or 'orthographic')
   */
  toggleProjection() {
    if (!this.controls) return this.projectionMode;

    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    const aspect = width / height;

    if (this.projectionMode === 'perspective') {
      // ------ Switch to orthographic ------
      this.projectionMode = 'orthographic';

      // Calculate frustum from perspective FOV at the current target distance
      const distance = this.camera.position.distanceTo(this.controls.target);
      const fovRad = this.camera.fov * (Math.PI / 180);
      const frustumHeight = 2 * distance * Math.tan(fovRad / 2);

      if (!this.orthoCamera) {
        this.orthoCamera = new OrthographicCamera(
          (frustumHeight * aspect) / -2,
          (frustumHeight * aspect) / 2,
          frustumHeight / 2,
          frustumHeight / -2,
          0.1,
          10000
        );
        this.orthoCamera.up.set(...PreviewManager.WORLD_UP);
      } else {
        this.orthoCamera.left = (frustumHeight * aspect) / -2;
        this.orthoCamera.right = (frustumHeight * aspect) / 2;
        this.orthoCamera.top = frustumHeight / 2;
        this.orthoCamera.bottom = frustumHeight / -2;
      }

      // Transfer pose from perspective camera
      this.orthoCamera.position.copy(this.camera.position);
      this.orthoCamera.up.copy(this.camera.up);
      this.orthoCamera.quaternion.copy(this.camera.quaternion);
      this.orthoCamera.zoom = 1;
      this.orthoCamera.updateProjectionMatrix();

      // Switch controls to orthographic camera
      this.controls.object = this.orthoCamera;
      this.controls.update();
    } else {
      // ------ Switch to perspective ------
      this.projectionMode = 'perspective';

      if (this.orthoCamera) {
        // Transfer pose back from orthographic camera
        this.camera.position.copy(this.orthoCamera.position);
        this.camera.up.copy(this.orthoCamera.up);
        this.camera.quaternion.copy(this.orthoCamera.quaternion);
      }

      this.camera.updateProjectionMatrix();

      // Switch controls back to perspective camera
      this.controls.object = this.camera;
      this.controls.update();
    }

    // Announce change to screen readers
    const modeName =
      this.projectionMode === 'perspective' ? 'Perspective' : 'Orthographic';
    this.announceCameraAction(`${modeName} projection`);

    // Switching projection need not move the camera, so OrbitControls emits no
    // 'change' — anything mirroring the projection has nothing to listen to.
    // Announced here so every caller (camera bar, View menu, the P shortcut)
    // reaches every mirror, the same shape as display-option-change.
    document.dispatchEvent(
      new CustomEvent('preview-projection-change', {
        detail: { mode: this.projectionMode },
      })
    );

    console.log(`[Preview] Switched to ${this.projectionMode} projection`);
    return this.projectionMode;
  }

  /**
   * Get current projection mode
   * @returns {string} 'perspective' or 'orthographic'
   */
  getProjectionMode() {
    return this.projectionMode;
  }

  /**
   * Get the currently active camera (perspective or orthographic)
   * @returns {THREE.Camera}
   */
  getActiveCamera() {
    return this.projectionMode === 'orthographic' && this.orthoCamera
      ? this.orthoCamera
      : this.camera;
  }

  /**
   * Adjust camera distance to maintain model's relative visual position
   * when the viewport aspect ratio changes significantly.
   *
   * This uses a compensation strategy based on how perspective projection
   * responds to aspect ratio changes:
   * - When going narrower (portrait): horizontal FOV shrinks, zoom out to fit
   * - When going wider (landscape): horizontal FOV expands, zoom in to maintain presence
   *
   * @param {number} previousAspect - Previous aspect ratio (width/height)
   * @param {number} newAspect - New aspect ratio (width/height)
   */
  _adjustCameraForAspectChange(previousAspect, newAspect) {
    if (!this.mesh || !this.controls) return;

    // Calculate the relative change in aspect ratio
    const aspectRatio = newAspect / previousAspect;

    const camera = this.getActiveCamera();

    // For orthographic cameras, adjust frustum instead of distance
    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      const frustumHeight =
        (this.orthoCamera.top - this.orthoCamera.bottom) /
        (this.orthoCamera.zoom || 1);
      this.orthoCamera.left = (frustumHeight * newAspect) / -2;
      this.orthoCamera.right = (frustumHeight * newAspect) / 2;
      this.orthoCamera.updateProjectionMatrix();
      this.controls.update();
      return;
    }

    const currentDistance = camera.position.distanceTo(this.controls.target);

    // Calculate adjustment factor
    // The idea: when aspect gets narrower, we need to zoom out (increase distance)
    // to keep the model from being horizontally clipped.
    // When aspect gets wider, we can zoom in slightly to maintain visual presence.
    //
    // Using square root provides a smoother, less aggressive adjustment that
    // works well across common aspect ratio transitions (e.g., 16:9 to 9:16)
    const adjustmentFactor = 1 / Math.sqrt(aspectRatio);

    // Apply a damping factor to prevent over-correction
    // This makes the adjustment less aggressive (70% of calculated adjustment)
    const dampedFactor = 1.0 + (adjustmentFactor - 1.0) * 0.7;

    // Calculate new distance with bounds checking
    const newDistance = currentDistance * dampedFactor;

    // Clamp to control limits with some margin
    const minDist = this.controls.minDistance * 1.2;
    const maxDist = this.controls.maxDistance * 0.9;
    const clampedDistance = Math.max(minDist, Math.min(maxDist, newDistance));

    // Only adjust if the change is meaningful (> 1% difference)
    if (Math.abs(clampedDistance - currentDistance) / currentDistance < 0.01) {
      return;
    }

    // Calculate direction vector from target to camera
    const direction = new Vector3()
      .subVectors(camera.position, this.controls.target)
      .normalize();

    camera.position
      .copy(this.controls.target)
      .addScaledVector(direction, clampedDistance);

    this.controls.update();

    console.log(
      `[Preview] Camera adjusted for aspect change: ${previousAspect.toFixed(2)} → ${newAspect.toFixed(2)}, distance: ${currentDistance.toFixed(1)} → ${clampedDistance.toFixed(1)}`
    );
  }

  /**
   * Set resize behavior configuration
   * @param {Object} config - Configuration options
   * @param {number} config.aspectChangeThreshold - Threshold for aspect change detection (0-1)
   * @param {boolean} config.adjustCameraOnResize - Whether to adjust camera on resize
   */
  setResizeConfig(config) {
    if (typeof config.aspectChangeThreshold === 'number') {
      this._resizeConfig.aspectChangeThreshold = Math.max(
        0.01,
        Math.min(0.5, config.aspectChangeThreshold)
      );
    }
    if (typeof config.adjustCameraOnResize === 'boolean') {
      this._resizeConfig.adjustCameraOnResize = config.adjustCameraOnResize;
    }
  }

  /**
   * Calculate dimensions from the current mesh
   * @returns {Object} Dimensions { x, y, z, volume, triangles }
   */
  calculateDimensions() {
    if (!this.mesh) return null;

    const box = new Box3().setFromObject(this.mesh);
    const size = box.getSize(new Vector3());
    const volume = size.x * size.y * size.z;
    const geo = this._getPrimaryGeometry();
    const triangles = geo
      ? geo.index
        ? geo.index.count / 3
        : geo.attributes.position.count / 3
      : 0;

    return {
      x: Math.round(size.x * 100) / 100, // Round to 2 decimal places
      y: Math.round(size.y * 100) / 100,
      z: Math.round(size.z * 100) / 100,
      volume: Math.round(volume * 100) / 100,
      triangles: Math.round(triangles),
    };
  }

  /**
   * Update the screen reader accessible model summary (WCAG 2.2)
   * This provides non-visual users with model dimensions
   */
  updateModelSummary() {
    const summaryEl = document.getElementById('previewModelSummary');
    if (!summaryEl) return;

    if (!this.mesh) {
      summaryEl.textContent =
        'No model loaded. Upload an OpenSCAD file and generate an STL to see the 3D preview.';
      return;
    }

    const dims = this.calculateDimensions();
    if (!dims) {
      summaryEl.textContent = '3D model loaded. Dimensions unavailable.';
      return;
    }

    summaryEl.textContent = `3D model loaded. ${dims.x} mm wide, ${dims.y} mm deep, ${dims.z} mm tall. ${dims.triangles.toLocaleString()} triangles.`;
  }

  /**
   * Pan camera and target in screen space
   * Uses the camera's own right/up axes so panning feels natural at any
   * viewing angle, including Top and Bottom views, where a world-up cross
   * product would be degenerate.
   * @param {number} deltaRight - Right/left movement in screen space
   * @param {number} deltaUp - Up/down movement in screen space
   */
  panCamera(deltaRight, deltaUp) {
    if (!this.controls) return;

    const camera = this.getActiveCamera();
    const right = new Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    const up = new Vector3().setFromMatrixColumn(camera.matrixWorld, 1);

    const panOffset = new Vector3()
      .addScaledVector(right, deltaRight)
      .addScaledVector(up, deltaUp);

    this.controls.target.add(panOffset);
    camera.position.add(panOffset);
    this.controls.update();
  }

  /**
   * Rotate camera horizontally around the target (Z-up)
   * Orbits around controls.target, not the world origin.
   * @param {number} angle - Rotation angle in radians (positive = left, negative = right)
   */
  rotateHorizontal(angle) {
    if (!this.controls) return;
    const camera = this.getActiveCamera();
    // Compute offset from the orbit target (not the world origin)
    const offset = camera.position.clone().sub(this.controls.target);
    // Rotate offset around the Z-axis (world up)
    offset.applyAxisAngle(new Vector3(0, 0, 1), angle);
    // Reposition camera at target + rotated offset
    camera.position.copy(this.controls.target).add(offset);
    this.controls.update();
  }

  /**
   * Rotate camera vertically around the target (Z-up)
   * Orbits around controls.target, not the world origin.
   * @param {number} angle - Rotation angle in radians (positive = up, negative = down)
   */
  rotateVertical(angle) {
    if (!this.controls) return;
    const camera = this.getActiveCamera();
    // Compute offset from the orbit target (not the world origin)
    const offset = camera.position.clone().sub(this.controls.target);
    const currentDist = offset.length();
    if (currentDist < 1e-6) return; // Degenerate case

    const horizontalAngle = Math.atan2(offset.y, offset.x);
    const verticalAngle = Math.asin(
      Math.max(-1, Math.min(1, offset.z / currentDist))
    );

    // Clamp to prevent flipping over the poles
    const newVerticalAngle = Math.max(
      -Math.PI / 2 + 0.01,
      Math.min(Math.PI / 2 - 0.01, verticalAngle + angle)
    );

    offset.x =
      currentDist * Math.cos(newVerticalAngle) * Math.cos(horizontalAngle);
    offset.y =
      currentDist * Math.cos(newVerticalAngle) * Math.sin(horizontalAngle);
    offset.z = currentDist * Math.sin(newVerticalAngle);

    camera.position.copy(this.controls.target).add(offset);
    this.controls.update();
  }

  /**
   * Zoom camera in or out
   * @param {number} amount - Zoom amount (positive = in, negative = out)
   */
  zoomCamera(amount) {
    if (!this.controls) return;
    if (this.projectionMode === 'orthographic' && this.orthoCamera) {
      // Orthographic: adjust zoom property (position changes don't affect apparent size)
      const factor = 1 + Math.abs(amount) * 0.02;
      this.orthoCamera.zoom *= amount > 0 ? factor : 1 / factor;
      this.orthoCamera.zoom = Math.max(0.01, this.orthoCamera.zoom);
      this.orthoCamera.updateProjectionMatrix();
      // controls.update() below only dispatches 'change' when the camera
      // position moved; an ortho zoom moves nothing, so the axis overlays'
      // zoom-rebuild listener would never hear it. The wheel path
      // through OrbitControls sets its own zoomChanged flag; this keyboard/
      // button path has to say so itself.
      this.controls.dispatchEvent?.({ type: 'change' });
    } else {
      // Perspective: translate camera along view direction
      const camera = this.getActiveCamera();
      const direction = new Vector3();
      camera.getWorldDirection(direction);
      camera.position.addScaledVector(direction, amount);
    }
    this.controls.update();
  }

  /**
   * Toggle measurement display
   * @param {boolean} enabled - Show or hide measurements
   */
  toggleMeasurements(enabled) {
    this.measurementsEnabled = enabled;
    this.saveMeasurementPreference(enabled);

    if (enabled && this.mesh) {
      this.showMeasurements();
    } else {
      this.hideMeasurements();
    }

    console.log(`[Preview] Measurements ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Show measurement overlays on the model
   */
  showMeasurements() {
    if (!this.mesh) return;

    this.hideMeasurements();

    this.dimensions = this.calculateDimensions();
    if (!this.dimensions) return;

    this.measurementHelpers = new Group();
    this.measurementHelpers.name = 'measurements';

    const box = new Box3().setFromObject(this.mesh);
    const min = box.min;
    const max = box.max;

    const lineColor = this.currentTheme.includes('dark') ? 0xff6b6b : 0xff0000;

    const boxHelper = new BoxHelper(this.mesh, lineColor);
    // Note: linewidth is ignored in WebGL, relying on color contrast instead
    this.measurementHelpers.add(boxHelper);

    // Add dimension lines and labels (we'll render text as sprites)
    this.addDimensionLine(
      new Vector3(min.x, min.y, min.z),
      new Vector3(max.x, min.y, min.z),
      `${this.dimensions.x} mm`,
      'X',
      lineColor
    );

    this.addDimensionLine(
      new Vector3(min.x, min.y, min.z),
      new Vector3(min.x, max.y, min.z),
      `${this.dimensions.y} mm`,
      'Y',
      lineColor
    );

    this.addDimensionLine(
      new Vector3(min.x, min.y, min.z),
      new Vector3(min.x, min.y, max.z),
      `${this.dimensions.z} mm`,
      'Z',
      lineColor
    );

    this.scene.add(this.measurementHelpers);
    console.log('[Preview] Measurements displayed:', this.dimensions);
  }

  /**
   * Add a dimension line with label
   * @param {THREE.Vector3} start - Start point
   * @param {THREE.Vector3} end - End point
   * @param {string} label - Dimension label
   * @param {string} axis - Axis name (X, Y, Z)
   * @param {number} color - Line color
   */
  addDimensionLine(start, end, label, axis, color) {
    // Create line geometry
    const points = [start, end];
    const geometry = new BufferGeometry().setFromPoints(points);
    // Note: linewidth is ignored in WebGL, relying on color contrast instead
    const material = new LineBasicMaterial({
      color: color,
    });
    const line = new Line(geometry, material);
    this.measurementHelpers.add(line);

    // Create text sprite for label, sized with the model
    const midpoint = new Vector3().lerpVectors(start, end, 0.5);
    const sprite = this.createTextSprite(label, color);
    const span = Math.max(
      Number(this.dimensions?.x) || 0,
      Number(this.dimensions?.y) || 0,
      Number(this.dimensions?.z) || 0
    );
    const width = dimensionLabelWidth(span);
    sprite.scale.set(width, width / 4, 1);

    // The width label hangs below its edge by one and a half times its height
    // (the top view sees it on the model's base plane, nearer the edge); the depth and
    // height labels stand beside the model, clear of its left edge by half
    // their width, instead of centered on the edge and half hidden by it.
    if (axis === 'X') midpoint.y -= width * 0.375;
    if (axis === 'Y') midpoint.x -= width * 0.625;
    if (axis === 'Z') midpoint.x -= width * 0.625;

    sprite.position.copy(midpoint);
    this.measurementHelpers.add(sprite);
  }

  /**
   * Create a text sprite for dimension labels
   * @param {string} text - Text content
   * @param {number} _color - Text color (unused, determined by theme)
   * @returns {THREE.Sprite} Text sprite
   */
  createTextSprite(text, _color) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const fontSize = this.highContrast ? 48 : 32;

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Configure text rendering
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = this.currentTheme.includes('dark')
      ? '#ffffff'
      : '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Draw background for better visibility
    const bgColor = this.currentTheme.includes('dark')
      ? 'rgba(0, 0, 0, 0.8)'
      : 'rgba(255, 255, 255, 0.8)';
    context.fillStyle = bgColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Draw text
    context.fillStyle = this.currentTheme.includes('dark')
      ? '#ffffff'
      : '#000000';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create sprite
    const spriteMaterial = new SpriteMaterial({ map: texture });
    const sprite = new Sprite(spriteMaterial);

    return sprite;
  }

  /**
   * Hide measurement overlays
   */
  hideMeasurements() {
    if (this.measurementHelpers) {
      // Dispose of geometries and materials
      this.measurementHelpers.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });

      this.scene.remove(this.measurementHelpers);
      this.measurementHelpers = null;
    }
  }

  /**
   * Load measurement preference from localStorage
   * @returns {boolean} Preference value
   */
  loadMeasurementPreference() {
    return readScopedPref(STORAGE_KEY_MEASUREMENTS) === 'true';
  }

  /**
   * Save measurement preference to localStorage
   * @param {boolean} enabled - Measurement enabled state
   */
  saveMeasurementPreference(enabled) {
    writeScopedPref(STORAGE_KEY_MEASUREMENTS, enabled ? 'true' : 'false');
  }

  /**
   * Toggle grid visibility
   * @param {boolean} enabled - Show or hide grid
   */
  toggleGrid(enabled) {
    this.gridEnabled = enabled;
    if (this.altViewGridActive) {
      safeSetItem(STORAGE_KEY_HFM_GRID, enabled ? 'true' : 'false');
    } else {
      this.saveGridPreference(enabled);
    }

    if (this.gridHelper) {
      this.gridHelper.visible = enabled;
    }

    console.log(`[Preview] Grid ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Switch the grid between the Alt View's own preference and the ordinary
   * one. At character resolution the grid draws as scattered punctuation,
   * so the Alt View starts without it unless it was turned on there.
   * @param {boolean} active - True while the Alt View is on
   */
  applyGridForAltView(active) {
    this.altViewGridActive = Boolean(active);
    this.gridEnabled = this._loadEffectiveGridPreference();
    if (this.gridHelper) {
      this.gridHelper.visible = this.gridEnabled;
    }
  }

  /**
   * @returns {boolean} The grid preference that applies now: the Alt
   *   View's own while it is on, the ordinary one otherwise
   */
  _loadEffectiveGridPreference() {
    return this.altViewGridActive
      ? safeGetItem(STORAGE_KEY_HFM_GRID) === 'true'
      : this.loadGridPreference();
  }

  /**
   * Load grid preference from localStorage
   * @returns {boolean} Preference value (defaults to true)
   */
  loadGridPreference() {
    // The Classic namespace's default is false (ui-scoped-prefs
    // NAMESPACE_DEFAULTS — the desktop shows no bed grid); a null read
    // only happens in Forge, where the grid defaults on.
    const pref = readScopedPref(STORAGE_KEY_GRID);
    return pref === null ? true : pref === 'true';
  }

  /**
   * Save grid preference to localStorage
   * @param {boolean} enabled - Grid enabled state
   */
  saveGridPreference(enabled) {
    writeScopedPref(STORAGE_KEY_GRID, enabled ? 'true' : 'false');
  }

  /**
   * Set a custom grid color, deriving the secondary color automatically.
   * @param {string} hexColor - CSS hex color (e.g. '#ff0000')
   */
  setGridColor(hexColor) {
    const hex = normalizeHexColor(hexColor);
    if (!hex) return;

    this.gridColorOverride = hex;
    this.saveGridColorPreference(hex);
    this._rebuildGrid();
  }

  /**
   * Remove the custom grid color, reverting to the current theme default.
   */
  resetGridColor() {
    this.gridColorOverride = null;
    this.saveGridColorPreference(null);
    this._rebuildGrid();
  }

  /**
   * @returns {string|null} The current grid color override, or null for theme default.
   */
  getGridColor() {
    return this.gridColorOverride;
  }

  /**
   * Return the resolved grid colors (override or theme default).
   * @returns {{ gridPrimary: number, gridSecondary: number }}
   */
  _resolveGridColors() {
    if (this.gridColorOverride) {
      const primary = parseInt(this.gridColorOverride.slice(1), 16);
      const secondary = PreviewManager._deriveSecondaryGridColor(
        primary,
        PREVIEW_COLORS[this.currentTheme]?.background ??
          PREVIEW_COLORS.light.background
      );
      return { gridPrimary: primary, gridSecondary: secondary };
    }
    const theme = PREVIEW_COLORS[this.currentTheme] || PREVIEW_COLORS.light;
    return {
      gridPrimary: theme.gridPrimary,
      gridSecondary: theme.gridSecondary,
    };
  }

  /**
   * Blend the primary grid color 50% toward the scene background to
   * produce a softer secondary grid line color.
   * @param {number} primary - 0xRRGGBB primary grid color
   * @param {number} background - 0xRRGGBB scene background color
   * @returns {number} 0xRRGGBB secondary color
   */
  static _deriveSecondaryGridColor(primary, background) {
    const blend = (a, b, t) => Math.round(a + (b - a) * t);
    const pR = (primary >> 16) & 0xff;
    const pG = (primary >> 8) & 0xff;
    const pB = primary & 0xff;
    const bR = (background >> 16) & 0xff;
    const bG = (background >> 8) & 0xff;
    const bB = background & 0xff;
    const r = blend(pR, bR, 0.5);
    const g = blend(pG, bG, 0.5);
    const b = blend(pB, bB, 0.5);
    return (r << 16) | (g << 8) | b;
  }

  /**
   * Load persisted grid color preference.
   * @returns {string|null}
   */
  loadGridColorPreference() {
    const raw = readScopedPref(STORAGE_KEY_GRID_COLOR);
    if (raw && /^#[0-9a-f]{6}$/i.test(raw)) return raw;
    return null;
  }

  /**
   * Persist grid color preference.
   * @param {string|null} hex
   */
  saveGridColorPreference(hex) {
    if (hex) {
      writeScopedPref(STORAGE_KEY_GRID_COLOR, hex);
    } else {
      removeScopedPref(STORAGE_KEY_GRID_COLOR);
    }
  }

  /**
   * Set grid line opacity (10–100). Values outside the range are clamped.
   * @param {number} percent - Opacity percentage
   */
  setGridOpacity(percent) {
    const clamped = Math.max(10, Math.min(100, Math.round(Number(percent))));
    if (Number.isNaN(clamped)) return;
    this.gridOpacity = clamped;
    this.saveGridOpacityPreference(clamped);
    this._applyGridOpacity();
  }

  /**
   * @returns {number} Current grid opacity (10–100).
   */
  getGridOpacity() {
    return this.gridOpacity;
  }

  /**
   * Reset grid opacity to 100% (fully opaque).
   */
  resetGridOpacity() {
    this.gridOpacity = 100;
    this.saveGridOpacityPreference(null);
    this._applyGridOpacity();
  }

  /**
   * Load persisted grid opacity preference.
   * @returns {number} 10–100 (default 100)
   */
  loadGridOpacityPreference() {
    const raw = readScopedPref(STORAGE_KEY_GRID_OPACITY);
    if (raw !== null) {
      const val = parseInt(raw, 10);
      if (!Number.isNaN(val) && val >= 10 && val <= 100) return val;
    }
    return 100;
  }

  /**
   * Persist grid opacity preference.
   * @param {number|null} value - null removes the key (revert to default 100)
   */
  saveGridOpacityPreference(value) {
    if (value !== null && value !== undefined && value !== 100) {
      writeScopedPref(STORAGE_KEY_GRID_OPACITY, String(value));
    } else {
      removeScopedPref(STORAGE_KEY_GRID_OPACITY);
    }
  }

  /**
   * Apply the current gridOpacity to the grid helper material.
   * @private
   */
  _applyGridOpacity() {
    if (!this.gridHelper) return;
    const opacity = this.gridOpacity / 100;
    const mat = this.gridHelper.material;
    if (Array.isArray(mat)) {
      mat.forEach((m) => {
        m.transparent = opacity < 1;
        m.opacity = opacity;
      });
    } else if (mat) {
      mat.transparent = opacity < 1;
      mat.opacity = opacity;
    }
  }

  /**
   * Rebuild the grid using current config, color override, and theme.
   * @private
   */
  _rebuildGrid() {
    if (!this.scene || !this.gridHelper) return;

    this.scene.remove(this.gridHelper);
    if (this.gridHelper.geometry) this.gridHelper.geometry.dispose();
    if (this.gridHelper.material) {
      if (Array.isArray(this.gridHelper.material)) {
        this.gridHelper.material.forEach((m) => m.dispose());
      } else {
        this.gridHelper.material.dispose();
      }
    }

    const colors = this._resolveGridColors();
    this.gridHelper = this._createGridHelper(colors);
    this.gridHelper.rotation.x = Math.PI / 2;
    this.gridHelper.visible = this.gridEnabled;
    this._applyGridOpacity();
    this.scene.add(this.gridHelper);
  }

  /**
   * Create a GridHelper using the current gridConfig dimensions.
   * Divisions are auto-calculated at 1 per 10mm for a clean appearance.
   * @param {Object} colors - Theme color object with gridPrimary/gridSecondary
   * @returns {THREE.GridHelper}
   */
  _createGridHelper(colors) {
    const { widthMm, heightMm } = this.gridConfig;
    // Use the larger dimension as the GridHelper size (it's square), and scale
    // height via geometry scaling so rectangular beds are represented correctly.
    const size = Math.max(widthMm, heightMm);
    const divisions = Math.round(size / 10);
    const helper = new GridHelper(
      size,
      divisions,
      colors.gridPrimary,
      colors.gridSecondary
    );
    // Stretch non-square beds by scaling the shorter axis
    helper.scale.set(widthMm / size, 1, heightMm / size);
    return helper;
  }

  /**
   * Load grid size from localStorage
   * @returns {{ widthMm: number, heightMm: number }}
   */
  loadGridSizePreference() {
    try {
      // try/catch retained for JSON.parse of possibly-corrupt values
      const raw = readScopedPref(STORAGE_KEY_GRID_SIZE);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed.widthMm === 'number' &&
          typeof parsed.heightMm === 'number'
        ) {
          return { widthMm: parsed.widthMm, heightMm: parsed.heightMm };
        }
      }
    } catch (_) {
      // Fall through to default
    }
    return { ...DEFAULT_GRID_CONFIG };
  }

  /**
   * Save grid size to localStorage
   * @param {{ widthMm: number, heightMm: number }} config
   */
  saveGridSizePreference(config) {
    writeScopedPref(STORAGE_KEY_GRID_SIZE, JSON.stringify(config));
  }

  /**
   * Return a copy of the current grid config
   * @returns {{ widthMm: number, heightMm: number }}
   */
  getGridSize() {
    return { ...this.gridConfig };
  }

  /**
   * Update the grid to new printer-bed dimensions and persist the preference.
   * @param {number} widthMm - Bed width in mm (50–500)
   * @param {number} heightMm - Bed height in mm (50–500)
   */
  setGridSize(widthMm, heightMm) {
    const clamp = (v) => Math.min(500, Math.max(50, Number(v) || 220));
    this.gridConfig = { widthMm: clamp(widthMm), heightMm: clamp(heightMm) };
    this.saveGridSizePreference(this.gridConfig);

    if (!this.scene || !this.gridHelper) return;

    // Remove and dispose old grid
    this.scene.remove(this.gridHelper);
    if (this.gridHelper.geometry) this.gridHelper.geometry.dispose();
    if (this.gridHelper.material) {
      if (Array.isArray(this.gridHelper.material)) {
        this.gridHelper.material.forEach((m) => m.dispose());
      } else {
        this.gridHelper.material.dispose();
      }
    }

    // Recreate with new size using resolved colors (custom override or theme)
    const gridColors = this._resolveGridColors();
    this.gridHelper = this._createGridHelper(gridColors);
    this.gridHelper.rotation.x = Math.PI / 2;
    this.gridHelper.visible = this.gridEnabled;
    this.scene.add(this.gridHelper);

    console.log(
      `[Preview] Grid size updated: ${this.gridConfig.widthMm}×${this.gridConfig.heightMm}mm`
    );
  }

  /**
   * Load user-saved custom grid presets from localStorage.
   * @returns {Array<{name: string, widthMm: number, heightMm: number}>}
   */
  loadCustomGridPresets() {
    try {
      // try/catch retained for JSON.parse of possibly-corrupt values
      const raw = safeGetItem(STORAGE_KEY_CUSTOM_GRID_PRESETS);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
    } catch (_) {
      // fall through
    }
    return [];
  }

  /**
   * Save a new custom grid preset. Validates bounds and name uniqueness.
   *
   * @param {string} name - User-provided preset name (non-empty, non-whitespace-only)
   * @param {number} widthMm - Width in mm (integer, 50–500)
   * @param {number} heightMm - Height in mm (integer, 50–500)
   * @returns {{ success: boolean, error?: string }}
   */
  saveCustomGridPreset(name, widthMm, heightMm) {
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      return { success: false, error: 'Preset name cannot be empty.' };
    }

    const w = Math.round(Number(widthMm));
    const h = Math.round(Number(heightMm));

    if (!Number.isFinite(w) || w < 50 || w > 500) {
      return {
        success: false,
        error: `Width must be an integer between 50 and 500 mm (got ${widthMm}).`,
      };
    }
    if (!Number.isFinite(h) || h < 50 || h > 500) {
      return {
        success: false,
        error: `Height must be an integer between 50 and 500 mm (got ${heightMm}).`,
      };
    }

    const existing = this.loadCustomGridPresets();

    if (existing.some((p) => p.name === trimmedName)) {
      return {
        success: false,
        error: `A custom preset named "${trimmedName}" already exists.`,
      };
    }

    existing.push({ name: trimmedName, widthMm: w, heightMm: h });

    if (
      !safeSetItem(STORAGE_KEY_CUSTOM_GRID_PRESETS, JSON.stringify(existing))
    ) {
      return {
        success: false,
        error: 'Could not save preset: storage unavailable or full.',
      };
    }

    return { success: true };
  }

  /**
   * Delete a user custom grid preset by name.
   * @param {string} name - Exact preset name to remove
   * @returns {boolean} True if a preset was removed
   */
  deleteCustomGridPreset(name) {
    const existing = this.loadCustomGridPresets();
    const next = existing.filter((p) => p.name !== name);
    if (next.length === existing.length) return false;

    return safeSetItem(STORAGE_KEY_CUSTOM_GRID_PRESETS, JSON.stringify(next));
  }

  /**
   * Toggle auto-bed feature (place object on Z=0 build plate)
   * @param {boolean} enabled - Enable or disable auto-bed
   */
  toggleAutoBed(enabled) {
    this.autoBedEnabled = enabled;
    this.saveAutoBedPreference(enabled);
    console.log(`[Preview] Auto-bed ${enabled ? 'enabled' : 'disabled'}`);

    // If we have a mesh, we need to reload to apply the change
    // Return true to indicate the model should be re-rendered
    return this.mesh !== null;
  }

  /**
   * Load auto-bed preference from localStorage
   * @returns {boolean} Preference value (defaults to true - most users want this)
   */
  loadAutoBedPreference() {
    const pref = readScopedPref(STORAGE_KEY_AUTO_BED);
    // Default to true (auto-bed enabled) if not set
    return pref === null ? true : pref === 'true';
  }

  /**
   * Save auto-bed preference to localStorage
   * @param {boolean} enabled - Auto-bed enabled state
   */
  saveAutoBedPreference(enabled) {
    writeScopedPref(STORAGE_KEY_AUTO_BED, enabled ? 'true' : 'false');
  }

  /**
   * Toggle mouse-wheel zoom-to-cursor behavior. When enabled, scrolling
   * over the preview zooms toward the pointer position; when disabled,
   * zoom is centered on the orbit target.
   * Keyboard zoom (`+` / `-`) is unaffected.
   *
   * @param {boolean} enabled
   */
  toggleZoomToCursor(enabled) {
    this.zoomToCursorEnabled = !!enabled;
    if (this.controls) {
      this.controls.zoomToCursor = this.zoomToCursorEnabled;
    }
    this.saveZoomToCursorPreference(this.zoomToCursorEnabled);
  }

  /**
   * Load zoom-to-cursor preference from localStorage.
   * Off unless chosen: each zoom toward a corner moves the orbit point off
   * the model, so the model then wanders as the view turns.
   * @returns {boolean}
   */
  loadZoomToCursorPreference() {
    return readScopedPref(STORAGE_KEY_ZOOM_TO_CURSOR) === 'true';
  }

  /**
   * The saved viewport color scheme id, falling back to the desktop default.
   * @returns {string}
   */
  loadViewportScheme() {
    const saved = readScopedPref(STORAGE_KEY_VIEWPORT_SCHEME);
    return VIEWPORT_SCHEMES.some((s) => s.id === saved)
      ? saved
      : DEFAULT_VIEWPORT_SCHEME;
  }

  /** @returns {string} */
  getViewportScheme() {
    return this.viewportScheme;
  }

  /**
   * Choose a desktop viewport color scheme and apply it now.
   *
   * Instant-apply, like the desktop: there is no OK/Cancel row in upstream's
   * dialog, so picking a name is the action.
   *
   * The scheme governs the Classic viewport. Outside Classic the app theme
   * drives these colors and keeps doing so, because overriding it would
   * also override the high-contrast and mono variants, which are
   * accessibility features rather than decoration. The tab says this in its
   * own text rather than leaving it as a silent divergence.
   *
   * @param {string} id
   * @returns {boolean} True when the id was known and stored
   */
  setViewportScheme(id) {
    if (!VIEWPORT_SCHEMES.some((s) => s.id === id)) return false;
    this.viewportScheme = id;
    writeScopedPref(STORAGE_KEY_VIEWPORT_SCHEME, id);

    // updateTheme() early-returns when the key is unchanged, so a repaint has
    // to go through detectTheme()'s fresh answer.
    const next = this.detectTheme();
    if (next !== this.currentTheme) {
      this.updateTheme(
        next,
        document.documentElement.getAttribute('data-high-contrast') === 'true'
      );
    }
    return true;
  }

  /**
   * @param {boolean} enabled
   */
  saveZoomToCursorPreference(enabled) {
    writeScopedPref(STORAGE_KEY_ZOOM_TO_CURSOR, enabled ? 'true' : 'false');
  }

  /**
   * The live swap: re-read every per-interface preview preference from the
   * newly active namespace and re-apply it to the running scene in one pass
   * (grid visibility, size, color and opacity, measurements, zoom-to-cursor
   * and the viewport scheme). Camera pose, mesh and parameters are
   * untouched: every interface shares them. Auto-bed's new value governs the
   * next geometry load; the placed mesh is not re-transformed mid-flip.
   * Colors follow separately via updateTheme(detectTheme()), which the
   * caller (syncPreviewSceneToMode) runs right after.
   */
  reloadScopedViewPreferences() {
    this.measurementsEnabled = this.loadMeasurementPreference();
    this.gridEnabled = this._loadEffectiveGridPreference();
    this.gridConfig = this.loadGridSizePreference();
    this.gridColorOverride = this.loadGridColorPreference();
    this.gridOpacity = this.loadGridOpacityPreference();
    this.autoBedEnabled = this.loadAutoBedPreference();
    this.zoomToCursorEnabled = this.loadZoomToCursorPreference();
    if (this.controls) {
      this.controls.zoomToCursor = this.zoomToCursorEnabled;
    }
    this.viewportScheme = this.loadViewportScheme();

    // One rebuild covers size, color, opacity and visibility; a no-op
    // before the first model (no scene yet — the constructor reads fresh).
    this._rebuildGrid();

    if (this.measurementsEnabled && this.mesh) {
      this.showMeasurements();
    } else {
      this.hideMeasurements();
    }
  }

  /**
   * Apply auto-bed transformation to geometry
   * Moves the geometry so its lowest Z point sits on Z=0 (the build plate)
   * @param {THREE.BufferGeometry} geometry - The geometry to transform
   */
  applyAutoBed(geometry) {
    // Reset offset tracking
    this.autoBedOffset = 0;

    if (!geometry || !geometry.attributes.position) {
      return;
    }

    const positions = geometry.attributes.position;
    const positionArray = positions.array;

    // Find minimum Z value
    let minZ = Infinity;
    for (let i = 2; i < positionArray.length; i += 3) {
      if (positionArray[i] < minZ) {
        minZ = positionArray[i];
      }
    }

    // If minZ is already 0 or very close, no need to translate
    if (Math.abs(minZ) < 0.0001) {
      console.log('[Preview] Auto-bed: Object already on build plate');
      return;
    }

    // Translate all Z coordinates up by -minZ (so minZ becomes 0)
    const offset = -minZ;
    for (let i = 2; i < positionArray.length; i += 3) {
      positionArray[i] += offset;
    }

    // Store the offset for rotation centering feature
    this.autoBedOffset = offset;

    // The edges overlay is built from segments stored beside the
    // geometry (the worker's, for a big mesh), in the frame the positions
    // were in when they were made. The positions just moved; the segments
    // move with them, or the overlay draws below the model by this offset.
    const segments = geometry.userData?.edgeSegments;
    if (segments && segments.length) {
      for (let i = 2; i < segments.length; i += 3) {
        segments[i] += offset;
      }
    }

    // Mark the position attribute as needing update
    positions.needsUpdate = true;

    // Recompute the bounding box/sphere since we modified positions
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    console.log(
      `[Preview] Auto-bed applied: translated Z by ${offset.toFixed(3)} mm`
    );
  }

  /**
   * Check if rotation centering is currently enabled
   * @returns {boolean} Whether rotation centering is active
   */
  isRotationCenteringEnabled() {
    return this.rotationCenteringEnabled;
  }

  /**
   * Enable auto-rotation of the model
   * Uses Three.js OrbitControls built-in autoRotate feature
   * @param {boolean} enabled - Whether auto-rotate should be enabled
   */
  setAutoRotate(enabled) {
    if (this.controls) {
      this.controls.autoRotate = enabled;
      console.log(`[Preview] Auto-rotate ${enabled ? 'enabled' : 'disabled'}`);
    }
  }

  /**
   * Check if auto-rotation is currently enabled
   * @returns {boolean} Whether auto-rotate is active
   */
  isAutoRotateEnabled() {
    return this.controls?.autoRotate ?? false;
  }

  /**
   * Set the auto-rotation speed
   * @param {number} speed - Rotation speed in degrees per second (0.1 to 3)
   */
  setAutoRotateSpeed(speed) {
    // Clamp to reasonable range: 0.1 to 3 degrees/second
    const clampedSpeed = Math.max(0.1, Math.min(3, speed));
    if (this.controls) {
      this.controls.autoRotateSpeed = clampedSpeed;
      console.log(`[Preview] Auto-rotate speed set to ${clampedSpeed} deg/s`);
    }
  }

  /**
   * Get the current auto-rotation speed
   * @returns {number} Current rotation speed in degrees per second
   */
  getAutoRotateSpeed() {
    // Default is 0.5 for a gentle rotation
    return this.controls?.autoRotateSpeed ?? 0.5;
  }

  // ============================================================================
  // Reference Overlay System
  // ============================================================================

  /**
   * Set the reference overlay source from an image/SVG
   * @param {Object} source - Source configuration
   * @param {string} source.kind - 'raster' (PNG/JPG) or 'svg'
   * @param {string} source.name - Source file name for display
   * @param {string} source.dataUrlOrText - Data URL for rasters, SVG text for SVG
   * @returns {Promise<void>}
   */
  async setReferenceOverlaySource({ kind, name, dataUrlOrText }) {
    // Dispose of existing texture
    if (this.referenceTexture) {
      this.referenceTexture.dispose();
      this.referenceTexture = null;
    }

    if (!dataUrlOrText) {
      console.log('[Preview] Overlay source cleared');
      this.overlayConfig.sourceFileName = null;
      this.overlayConfig.intrinsicAspect = null;
      this._lastSvgContent = null;
      this.removeReferenceOverlay();
      return;
    }

    try {
      if (kind === 'svg') {
        this._lastSvgContent = dataUrlOrText;
        const { texture, aspect, widthMm, heightMm } =
          await this.svgTextToCanvasTexture(
            dataUrlOrText,
            this.overlayConfig.svgColor
          );
        this.referenceTexture = texture;
        this.overlayConfig.intrinsicAspect = aspect;

        // Use the SVG's physical mm dimensions (computed via the same 96 DPI
        // convention that OpenSCAD desktop uses for SVG import). This ensures
        // the overlay is sized identically to how OpenSCAD interprets the file.
        if (widthMm && heightMm) {
          this.overlayConfig.width = widthMm;
          this.overlayConfig.height = heightMm;
        } else if (this.overlayConfig.lockAspect && aspect) {
          this.overlayConfig.height = this.overlayConfig.width / aspect;
        }
      } else {
        this._lastSvgContent = null;
        const { texture, aspect } =
          await this.rasterDataUrlToTexture(dataUrlOrText);
        this.referenceTexture = texture;
        this.overlayConfig.intrinsicAspect = aspect;

        // Raster images have no inherent physical size — keep current width
        // and derive height from aspect ratio
        if (this.overlayConfig.lockAspect && aspect) {
          this.overlayConfig.height = this.overlayConfig.width / aspect;
        }
      }

      this.overlayConfig.sourceFileName = name;

      // Create/update the overlay if enabled
      if (this.overlayConfig.enabled) {
        this.createOrUpdateReferenceOverlay();
      }

      console.log(
        `[Preview] Overlay source set: ${name} (${kind}, ` +
          `${this.overlayConfig.width.toFixed(1)} × ${this.overlayConfig.height.toFixed(1)} mm, ` +
          `aspect: ${this.overlayConfig.intrinsicAspect?.toFixed(2) || 'unknown'})`
      );
    } catch (error) {
      console.error('[Preview] Failed to load overlay source:', error);
      throw error;
    }
  }

  /**
   * Parse an SVG length value (e.g. "200", "200px", "175.6mm", "2in") and
   * return the equivalent size in millimeters using the same 96 DPI default
   * that OpenSCAD desktop uses for SVG import.
   *
   * @param {string|null} raw - Raw attribute value (may include unit suffix)
   * @returns {number|null} Size in mm, or null if unparseable
   */
  static svgLengthToMm(raw) {
    if (!raw) return null;
    const str = String(raw).trim();
    if (!str) return null;

    // OpenSCAD default: 96 user-units per inch (CSS reference pixel)
    const OPENSCAD_DPI = 96;
    const MM_PER_INCH = 25.4;

    // CSS absolute-length units → mm conversion factors
    const unitFactors = {
      mm: 1,
      cm: 10,
      in: MM_PER_INCH,
      pt: MM_PER_INCH / 72, // 1pt = 1/72 inch
      pc: MM_PER_INCH / 6, // 1pc = 1/6 inch
      px: MM_PER_INCH / OPENSCAD_DPI, // 1px = 1/96 inch
    };

    const match = str.match(
      /^([+-]?\d*\.?\d+(?:e[+-]?\d+)?)\s*(mm|cm|in|pt|pc|px)?$/i
    );
    if (!match) return null;

    const value = parseFloat(match[1]);
    if (!isFinite(value) || value <= 0) return null;

    const unit = (match[2] || '').toLowerCase();

    // Unitless values are treated as user-units at 96 DPI (same as px)
    const factor = unitFactors[unit] || unitFactors.px;
    return value * factor;
  }

  /**
   * Convert SVG text to a Three.js CanvasTexture.
   * When recolorHex is provided, all non-transparent pixels are replaced
   * with that color — making dark SVGs visible on dark backgrounds.
   *
   * Also computes the physical mm dimensions of the SVG using the same
   * 96 DPI convention that OpenSCAD desktop uses for SVG import, so the
   * overlay can be sized to match the real-world measurements the SVG
   * was saved with.
   *
   * @param {string} svgContent - SVG markup
   * @param {string|null} [recolorHex=null] - CSS hex color (e.g. '#ffffff')
   * @param {{ targetCanvasSize?: number }} [opts] - Optional overrides
   * @returns {Promise<{texture: THREE.CanvasTexture, aspect: number, widthMm: number|null, heightMm: number|null}>}
   */
  async svgTextToCanvasTexture(svgContent, recolorHex = null, opts = {}) {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgEl = svgDoc.querySelector('svg');

    if (!svgEl) {
      throw new Error('Invalid SVG: no <svg> element found');
    }

    // --- Determine pixel dimensions for canvas rendering ---
    let intrinsicWidth, intrinsicHeight;

    const viewBox = svgEl.getAttribute('viewBox');
    if (viewBox) {
      const [, , vbWidth, vbHeight] = viewBox.split(/[\s,]+/).map(parseFloat);
      intrinsicWidth = vbWidth;
      intrinsicHeight = vbHeight;
    } else {
      intrinsicWidth =
        parseFloat(svgEl.getAttribute('width')) ||
        parseFloat(svgEl.style.width) ||
        200;
      intrinsicHeight =
        parseFloat(svgEl.getAttribute('height')) ||
        parseFloat(svgEl.style.height) ||
        150;
    }

    const aspect = intrinsicWidth / intrinsicHeight;

    // --- Compute physical mm size (OpenSCAD 96 DPI convention) ---
    // Priority: explicit width/height with units → viewBox at 96 DPI
    const rawW = svgEl.getAttribute('width');
    const rawH = svgEl.getAttribute('height');
    let widthMm = PreviewManager.svgLengthToMm(rawW);
    let heightMm = PreviewManager.svgLengthToMm(rawH);

    // If width/height are missing or unparseable, fall back to viewBox
    // dimensions treated as user-units at 96 DPI (OpenSCAD default).
    if (widthMm === null && viewBox) {
      const OPENSCAD_DPI = 96;
      const [, , vbW] = viewBox.split(/[\s,]+/).map(parseFloat);
      widthMm = (vbW * 25.4) / OPENSCAD_DPI;
    }
    if (heightMm === null && viewBox) {
      const OPENSCAD_DPI = 96;
      const [, , , vbH] = viewBox.split(/[\s,]+/).map(parseFloat);
      heightMm = (vbH * 25.4) / OPENSCAD_DPI;
    }

    // Determine canvas resolution (bounded to avoid memory spikes).
    // When targetCanvasSize is specified, scale the canvas so the longest
    // edge reaches that size — producing crisp textures for SVG content
    // that has small viewBox dimensions (e.g. 246×170 from OpenSCAD).
    const maxDim = opts.targetCanvasSize || this.getMaxTextureResolution();
    let canvasWidth, canvasHeight;

    if (opts.targetCanvasSize) {
      if (intrinsicWidth >= intrinsicHeight) {
        canvasWidth = maxDim;
        canvasHeight = maxDim / aspect;
      } else {
        canvasHeight = maxDim;
        canvasWidth = maxDim * aspect;
      }
    } else if (intrinsicWidth >= intrinsicHeight) {
      canvasWidth = Math.min(intrinsicWidth, maxDim);
      canvasHeight = canvasWidth / aspect;
    } else {
      canvasHeight = Math.min(intrinsicHeight, maxDim);
      canvasWidth = canvasHeight * aspect;
    }

    // Create canvas at the computed resolution
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(canvasWidth);
    canvas.height = Math.ceil(canvasHeight);

    // Create Image from SVG blob
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    try {
      const img = await this.loadImage(url);
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Recolor: replace all non-transparent pixels with the chosen color.
      // Uses 'source-in' compositing so alpha/shape is preserved.
      if (recolorHex) {
        ctx.globalCompositeOperation = 'source-in';
        ctx.fillStyle = recolorHex;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = 'source-over';
      }
    } finally {
      URL.revokeObjectURL(url);
    }

    // Create Three.js texture from canvas
    const texture = new CanvasTexture(canvas);
    texture.needsUpdate = true;

    return { texture, aspect, widthMm, heightMm };
  }

  /**
   * Load a raster image (PNG/JPG) from a data URL to a Three.js texture
   * @param {string} dataUrl - Data URL of the image
   * @returns {Promise<{texture: THREE.Texture, aspect: number}>}
   */
  async rasterDataUrlToTexture(dataUrl) {
    const img = await this.loadImage(dataUrl);
    const aspect = img.width / img.height;

    // Resize if needed to avoid memory issues
    const maxDim = this.getMaxTextureResolution();
    let canvas;

    if (img.width > maxDim || img.height > maxDim) {
      canvas = document.createElement('canvas');
      if (img.width >= img.height) {
        canvas.width = maxDim;
        canvas.height = maxDim / aspect;
      } else {
        canvas.height = maxDim;
        canvas.width = maxDim * aspect;
      }
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const texture = new CanvasTexture(canvas);
      texture.needsUpdate = true;
      return { texture, aspect };
    }

    // Use TextureLoader for full-res images
    const texture = new Texture(img);
    texture.needsUpdate = true;
    return { texture, aspect };
  }

  /**
   * Load an image from a URL
   * @param {string} url - URL to load
   * @returns {Promise<HTMLImageElement>}
   */
  loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = url;
    });
  }

  /**
   * Get the maximum texture resolution based on device capability
   * @returns {number} Maximum dimension in pixels
   */
  getMaxTextureResolution() {
    // Mobile devices get lower resolution to avoid memory issues
    const isMobile = window.innerWidth < 768 || navigator.maxTouchPoints > 0;
    return isMobile ? 1024 : 2048;
  }

  /**
   * Create or update the reference overlay plane mesh
   */
  createOrUpdateReferenceOverlay() {
    if (!this.scene || !this.referenceTexture) {
      return;
    }

    const { width, height, opacity, offsetX, offsetY, rotationDeg, zPosition } =
      this.overlayConfig;

    if (!this.referenceOverlay) {
      const geometry = new PlaneGeometry(width, height);
      const material = new MeshBasicMaterial({
        map: this.referenceTexture,
        transparent: true,
        opacity: opacity,
        depthWrite: false, // Prevent overlay from occluding other objects
        side: DoubleSide,
      });

      this.referenceOverlay = new Mesh(geometry, material);
      this.referenceOverlay.name = 'referenceOverlay';

      // Position on XY plane (normal +Z) for Z-up coordinate system
      this.referenceOverlay.rotation.x = 0; // Already on XY plane
      this.scene.add(this.referenceOverlay);

      console.log('[Preview] Reference overlay created');
    } else {
      // Update geometry if size changed
      const geo = this.referenceOverlay.geometry;
      if (geo.parameters.width !== width || geo.parameters.height !== height) {
        geo.dispose();
        this.referenceOverlay.geometry = new PlaneGeometry(width, height);
      }

      this.referenceOverlay.material.map = this.referenceTexture;
      this.referenceOverlay.material.opacity = opacity;
      this.referenceOverlay.material.needsUpdate = true;
    }

    this.referenceOverlay.position.set(offsetX, offsetY, zPosition);
    this.referenceOverlay.rotation.z = (rotationDeg * Math.PI) / 180;

    // Apply rotation centering offset if active
    if (this.rotationCenteringEnabled) {
      this.referenceOverlay.position.z = zPosition - this.autoBedOffset;
    }

    this.referenceOverlay.visible = this.overlayConfig.enabled;
  }

  /**
   * Fit the overlay size to match the current model's XY bounding box
   */
  fitOverlayToModelXY() {
    if (!this.mesh) {
      console.log('[Preview] No mesh to fit overlay to');
      return;
    }

    const box = new Box3().setFromObject(this.mesh);
    const size = box.getSize(new Vector3());

    // Update overlay dimensions to match model XY
    this.overlayConfig.width = size.x;
    this.overlayConfig.height = size.y;

    // If aspect is locked and we have an intrinsic aspect, adjust to fit within bounds
    if (this.overlayConfig.lockAspect && this.overlayConfig.intrinsicAspect) {
      const modelAspect = size.x / size.y;
      const imageAspect = this.overlayConfig.intrinsicAspect;

      if (imageAspect > modelAspect) {
        // Image is wider than model, fit to width
        this.overlayConfig.width = size.x;
        this.overlayConfig.height = size.x / imageAspect;
      } else {
        // Image is taller than model, fit to height
        this.overlayConfig.height = size.y;
        this.overlayConfig.width = size.y * imageAspect;
      }
    }

    // Center the overlay under the model
    this.overlayConfig.offsetX = 0;
    this.overlayConfig.offsetY = 0;

    if (this.overlayConfig.enabled) {
      this.createOrUpdateReferenceOverlay();
    }

    console.log(
      `[Preview] Overlay fitted to model XY: ${this.overlayConfig.width.toFixed(1)} x ${this.overlayConfig.height.toFixed(1)} mm`
    );
  }

  /**
   * Resize the reference overlay to match explicit physical screen dimensions (mm).
   * Use this to snap the overlay to a known tablet screen size selected from the
   * tablet database (public/data/tablets.json) or from SCAD parameters.
   *
   * @param {number} widthMm - Screen width in mm
   * @param {number} heightMm - Screen height in mm
   */
  fitOverlayToScreenDimensions(widthMm, heightMm) {
    if (
      typeof widthMm !== 'number' ||
      typeof heightMm !== 'number' ||
      widthMm <= 0 ||
      heightMm <= 0
    ) {
      console.warn(
        '[Preview] fitOverlayToScreenDimensions: invalid dimensions',
        widthMm,
        heightMm
      );
      return;
    }

    this.overlayConfig.width = widthMm;
    this.overlayConfig.height = heightMm;
    this.overlayConfig.offsetX = 0;
    this.overlayConfig.offsetY = 0;

    if (this.overlayConfig.enabled) {
      this.createOrUpdateReferenceOverlay();
    }

    console.log(
      `[Preview] Overlay sized to screen dimensions: ${widthMm} x ${heightMm} mm`
    );
  }

  /**
   * Enable or disable the reference overlay visibility
   * @param {boolean} enabled - Whether the overlay should be visible
   */
  setOverlayEnabled(enabled) {
    this.overlayConfig.enabled = enabled;

    if (enabled && this.referenceTexture) {
      this.createOrUpdateReferenceOverlay();
      // Refresh overlay measurements if they're enabled
      if (this.overlayMeasurementsEnabled) {
        this.showOverlayMeasurements();
      }
    } else if (this.referenceOverlay) {
      this.referenceOverlay.visible = false;
      // Hide overlay measurements when overlay is disabled
      this.hideOverlayMeasurements();
    }

    console.log(`[Preview] Overlay ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Set the overlay opacity (0-1)
   * @param {number} opacity01 - Opacity value from 0 to 1
   */
  setOverlayOpacity(opacity01) {
    this.overlayConfig.opacity = Math.max(0, Math.min(1, opacity01));

    if (this.referenceOverlay && this.referenceOverlay.material) {
      this.referenceOverlay.material.opacity = this.overlayConfig.opacity;
    }
  }

  /**
   * Set the SVG recolor hex and re-rasterise the current overlay if it is SVG.
   * Pass null to revert to original SVG colors.
   * @param {string|null} hexColor - CSS hex color (e.g. '#ffffff') or null
   */
  async setOverlaySvgColor(hexColor) {
    this.overlayConfig.svgColor = hexColor || null;

    // Re-rasterise if the current source is SVG
    if (this._lastSvgContent) {
      if (this.referenceTexture) {
        this.referenceTexture.dispose();
        this.referenceTexture = null;
      }
      const { texture, aspect } = await this.svgTextToCanvasTexture(
        this._lastSvgContent,
        this.overlayConfig.svgColor
      );
      this.referenceTexture = texture;
      this.overlayConfig.intrinsicAspect = aspect;

      if (this.overlayConfig.enabled) {
        this.createOrUpdateReferenceOverlay();
      }
    }
  }

  /**
   * Set the overlay transform (position and rotation)
   * @param {Object} transform - Transform configuration
   * @param {number} [transform.offsetX] - X offset in mm
   * @param {number} [transform.offsetY] - Y offset in mm
   * @param {number} [transform.rotationDeg] - Rotation in degrees
   */
  setOverlayTransform({ offsetX, offsetY, rotationDeg }) {
    if (typeof offsetX === 'number') {
      this.overlayConfig.offsetX = offsetX;
    }
    if (typeof offsetY === 'number') {
      this.overlayConfig.offsetY = offsetY;
    }
    if (typeof rotationDeg === 'number') {
      this.overlayConfig.rotationDeg = rotationDeg;
    }

    if (this.overlayConfig.enabled) {
      this.createOrUpdateReferenceOverlay();
      // Refresh measurements if enabled
      if (this.overlayMeasurementsEnabled) {
        this.showOverlayMeasurements();
      }
    }
  }

  /**
   * The Z a preset resolves to, in mm, for the model currently loaded.
   *
   * 'model-top' asks the mesh, so it is only meaningful while there is one;
   * with no model it falls back to the build plate rather than to a stale
   * height from a previous object.
   *
   * @param {string} preset
   * @param {number} customMm - Used only by the 'custom' preset
   * @returns {number} Z in mm
   */
  resolveOverlayZ(preset, customMm) {
    switch (preset) {
      case 'build-plate':
        return 0;
      case 'model-top': {
        if (!this.mesh) return 0;
        const box = new Box3().setFromObject(this.mesh);
        if (!Number.isFinite(box.max.z)) return 0;
        // A hair above the face, or it z-fights the surface it is tracing.
        return box.max.z + OVERLAY_SURFACE_EPSILON_MM;
      }
      case 'custom':
        return Number.isFinite(customMm) ? customMm : 0;
      case 'under-plate':
      default:
        return -0.25;
    }
  }

  /**
   * Choose which surface the overlay sits against.
   *
   * @param {Object} options
   * @param {string} [options.preset] - under-plate | build-plate | model-top | custom
   * @param {number} [options.customMm] - Z in mm, used by the custom preset
   */
  setOverlayZ({ preset, customMm } = {}) {
    if (typeof preset === 'string') this.overlayConfig.zPreset = preset;
    if (Number.isFinite(customMm)) this.overlayConfig.zCustomMm = customMm;
    this.overlayConfig.zPosition = this.resolveOverlayZ(
      this.overlayConfig.zPreset,
      this.overlayConfig.zCustomMm
    );
    if (this.overlayConfig.enabled) {
      this.createOrUpdateReferenceOverlay();
      if (this.overlayMeasurementsEnabled) {
        this.showOverlayMeasurements();
      }
    }
  }

  /**
   * Re-resolve the overlay Z against whatever model is loaded now.
   * Called after a render, so 'Top of model' follows the object.
   */
  refreshOverlayZ() {
    if (this.overlayConfig.zPreset !== 'model-top') return;
    this.setOverlayZ({});
  }

  /**
   * Set the overlay size
   * @param {Object} size - Size configuration
   * @param {number} [size.width] - Width in mm
   * @param {number} [size.height] - Height in mm
   */
  setOverlaySize({ width, height }) {
    if (typeof width === 'number') {
      this.overlayConfig.width = width;
      // Adjust height if aspect is locked
      if (this.overlayConfig.lockAspect && this.overlayConfig.intrinsicAspect) {
        this.overlayConfig.height = width / this.overlayConfig.intrinsicAspect;
      }
    }
    if (typeof height === 'number') {
      this.overlayConfig.height = height;
      // Adjust width if aspect is locked
      if (this.overlayConfig.lockAspect && this.overlayConfig.intrinsicAspect) {
        this.overlayConfig.width = height * this.overlayConfig.intrinsicAspect;
      }
    }

    if (this.overlayConfig.enabled) {
      this.createOrUpdateReferenceOverlay();
      // Refresh measurements if enabled
      if (this.overlayMeasurementsEnabled) {
        this.showOverlayMeasurements();
      }
    }
  }

  /**
   * Set whether aspect ratio is locked for the overlay
   * @param {boolean} locked - Whether to lock aspect ratio
   */
  setOverlayAspectLock(locked) {
    this.overlayConfig.lockAspect = locked;
  }

  /**
   * Remove and dispose the reference overlay
   */
  removeReferenceOverlay() {
    // Hide overlay measurements first
    this.hideOverlayMeasurements();

    if (this.referenceOverlay) {
      this.scene.remove(this.referenceOverlay);
      this.referenceOverlay.geometry.dispose();
      this.referenceOverlay.material.dispose();
      this.referenceOverlay = null;
      console.log('[Preview] Reference overlay removed');
    }

    if (this.referenceTexture) {
      this.referenceTexture.dispose();
      this.referenceTexture = null;
    }
  }

  /**
   * Get the current overlay configuration
   * @returns {Object} Copy of the overlay configuration
   */
  getOverlayConfig() {
    return { ...this.overlayConfig };
  }

  /**
   * Toggle overlay measurement display
   * @param {boolean} enabled - Show or hide overlay measurements
   */
  toggleOverlayMeasurements(enabled) {
    this.overlayMeasurementsEnabled = enabled;

    if (enabled && this.referenceOverlay && this.overlayConfig.enabled) {
      this.showOverlayMeasurements();
    } else {
      this.hideOverlayMeasurements();
    }

    console.log(
      `[Preview] Overlay measurements ${enabled ? 'enabled' : 'disabled'}`
    );
  }

  /**
   * Show measurement overlays on the reference overlay plane
   */
  showOverlayMeasurements() {
    if (!this.referenceOverlay || !this.overlayConfig.enabled) return;

    // Remove existing overlay measurements
    this.hideOverlayMeasurements();

    // Create group for overlay measurement visuals
    this.overlayMeasurementHelpers = new Group();
    this.overlayMeasurementHelpers.name = 'overlayMeasurements';

    const { width, height, offsetX, offsetY, zPosition, rotationDeg } =
      this.overlayConfig;

    // Calculate corners of the overlay (accounting for rotation centering)
    const z = this.rotationCenteringEnabled
      ? zPosition - this.autoBedOffset
      : zPosition;
    const zLabel = z + 0.1; // Just slightly above overlay plane

    // Center point
    const cx = offsetX;
    const cy = offsetY;

    // Half dimensions
    const hw = width / 2;
    const hh = height / 2;

    // Label offset distance from overlay edge (mm) - far enough to be visible
    const labelOffset = Math.max(15, Math.max(width, height) * 0.1);

    // Rotation in radians
    const rot = (rotationDeg * Math.PI) / 180;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);

    // Helper to rotate a point around center
    const rotatePoint = (x, y) => ({
      x: cx + (x - cx) * cos - (y - cy) * sin,
      y: cy + (x - cx) * sin + (y - cy) * cos,
    });

    // Calculate corners (after rotation)
    const corners = [
      rotatePoint(cx - hw, cy - hh), // bottom-left
      rotatePoint(cx + hw, cy - hh), // bottom-right
      rotatePoint(cx + hw, cy + hh), // top-right
      rotatePoint(cx - hw, cy + hh), // top-left
    ];

    // Choose color - use a different color than model measurements (cyan/teal)
    const lineColor = this.currentTheme.includes('dark') ? 0x00ffff : 0x008b8b;

    // Create outline edges
    for (let i = 0; i < 4; i++) {
      const start = corners[i];
      const end = corners[(i + 1) % 4];
      const points = [
        new Vector3(start.x, start.y, zLabel),
        new Vector3(end.x, end.y, zLabel),
      ];
      const geometry = new BufferGeometry().setFromPoints(points);
      const material = new LineBasicMaterial({ color: lineColor });
      const line = new Line(geometry, material);
      this.overlayMeasurementHelpers.add(line);
    }

    // Calculate label positions outside the overlay bounds
    // Width label: below the bottom edge
    const bottomMid = {
      x: (corners[0].x + corners[1].x) / 2,
      y: (corners[0].y + corners[1].y) / 2,
    };
    // Offset perpendicular to bottom edge (outward)
    const bottomDir = {
      x: -(corners[1].y - corners[0].y),
      y: corners[1].x - corners[0].x,
    };
    const bottomLen = Math.sqrt(
      bottomDir.x * bottomDir.x + bottomDir.y * bottomDir.y
    );
    const widthLabelPos = {
      x: bottomMid.x - (bottomDir.x / bottomLen) * labelOffset,
      y: bottomMid.y - (bottomDir.y / bottomLen) * labelOffset,
    };

    // Height label: left of the left edge
    const leftMid = {
      x: (corners[0].x + corners[3].x) / 2,
      y: (corners[0].y + corners[3].y) / 2,
    };
    // Offset perpendicular to left edge (outward)
    const leftDir = {
      x: -(corners[3].y - corners[0].y),
      y: corners[3].x - corners[0].x,
    };
    const leftLen = Math.sqrt(leftDir.x * leftDir.x + leftDir.y * leftDir.y);
    const heightLabelPos = {
      x: leftMid.x - (leftDir.x / leftLen) * labelOffset,
      y: leftMid.y - (leftDir.y / leftLen) * labelOffset,
    };

    // Create flat text labels (lying on XY plane)
    const widthLabel = this.createFlatTextLabel(
      `${Math.round(width)} mm`,
      lineColor,
      rotationDeg
    );
    widthLabel.position.set(widthLabelPos.x, widthLabelPos.y, zLabel);
    this.overlayMeasurementHelpers.add(widthLabel);

    const heightLabel = this.createFlatTextLabel(
      `${Math.round(height)} mm`,
      lineColor,
      rotationDeg + 90 // Rotate 90° for height label
    );
    heightLabel.position.set(heightLabelPos.x, heightLabelPos.y, zLabel);
    this.overlayMeasurementHelpers.add(heightLabel);

    // Add dimension lines from label to edge
    this.addDimensionExtensionLine(widthLabelPos, bottomMid, zLabel, lineColor);
    this.addDimensionExtensionLine(heightLabelPos, leftMid, zLabel, lineColor);

    this.scene.add(this.overlayMeasurementHelpers);
    console.log(
      `[Preview] Overlay measurements displayed: ${Math.round(width)} x ${Math.round(height)} mm`
    );
  }

  /**
   * Create a flat text label that lies on the XY plane
   * @param {string} text - Text content
   * @param {number} color - Text color
   * @param {number} rotationDeg - Rotation in degrees
   * @returns {THREE.Mesh} Text mesh lying flat
   */
  createFlatTextLabel(text, color, rotationDeg) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const fontSize = 48;

    // Set canvas size
    canvas.width = 256;
    canvas.height = 64;

    // Draw background for better visibility
    const bgColor = this.currentTheme.includes('dark')
      ? 'rgba(0, 0, 0, 0.85)'
      : 'rgba(255, 255, 255, 0.85)';
    context.fillStyle = bgColor;
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Configure text rendering
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = this.currentTheme.includes('dark')
      ? '#00ffff'
      : '#008b8b';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);

    // Create texture from canvas
    const texture = new CanvasTexture(canvas);
    texture.needsUpdate = true;

    // Create a plane geometry lying flat on XY
    // Size the plane proportionally to the text (roughly 20mm wide for readability)
    const planeWidth = 30;
    const planeHeight = planeWidth * (canvas.height / canvas.width);
    const geometry = new PlaneGeometry(planeWidth, planeHeight);

    const material = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: DoubleSide,
      depthTest: true,
      depthWrite: false,
    });

    const mesh = new Mesh(geometry, material);

    // Rotate to lie flat on XY plane (facing up, +Z)
    // PlaneGeometry faces +Z by default, so no X rotation needed
    // Apply the overlay's rotation around Z axis
    mesh.rotation.z = (rotationDeg * Math.PI) / 180;

    return mesh;
  }

  /**
   * Add a thin extension line from label to edge
   * @param {Object} from - Start position {x, y}
   * @param {Object} to - End position {x, y}
   * @param {number} z - Z position
   * @param {number} color - Line color
   */
  addDimensionExtensionLine(from, to, z, color) {
    const points = [new Vector3(from.x, from.y, z), new Vector3(to.x, to.y, z)];
    const geometry = new BufferGeometry().setFromPoints(points);
    const material = new LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: 0.5,
    });
    const line = new Line(geometry, material);
    this.overlayMeasurementHelpers.add(line);
  }

  /**
   * Hide overlay measurement overlays
   */
  hideOverlayMeasurements() {
    if (this.overlayMeasurementHelpers) {
      // Dispose of geometries and materials
      this.overlayMeasurementHelpers.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (child.material.map) child.material.map.dispose();
          child.material.dispose();
        }
      });

      this.scene.remove(this.overlayMeasurementHelpers);
      this.overlayMeasurementHelpers = null;
    }
  }

  /**
   * Check if overlay measurements are enabled
   * @returns {boolean}
   */
  isOverlayMeasurementsEnabled() {
    return this.overlayMeasurementsEnabled;
  }

  /**
   * Clear the preview
   */
  clear() {
    this.hide2DPreview();
    this.hideMeasurements();
    this.clearBackgroundShapes();
    this.dimensions = null;

    // Reset rotation centering state
    this.rotationCenteringEnabled = false;
    this.autoBedOffset = 0;
    this._modelCenter = null;

    if (this.mesh) {
      this.scene.remove(this.mesh);
      this._disposeMeshResources();
      this.mesh = null;
    }

    // Keep the overlay when clearing the model (user may want to reference it for alignment)
    // But update its Z position since auto-bed offset is reset
    if (this.referenceOverlay) {
      this.referenceOverlay.position.z = this.overlayConfig.zPosition;
    }

    if (this.renderer) this.renderer.render(this.scene, this.getActiveCamera());

    // Update screen reader model summary (WCAG 2.2)
    this.updateModelSummary();
  }

  /**
   * Dispose of all resources
   */
  dispose() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Cancel any pending debounced resize
    if (this._resizeDebounceId) {
      cancelAnimationFrame(this._resizeDebounceId);
      this._resizeDebounceId = null;
    }

    if (this._debouncedResize) {
      window.removeEventListener('resize', this._debouncedResize);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clean up keyboard controls
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
      this.keyboardHandler = null;
    }

    if (this.mesh) {
      this._disposeMeshResources();
    }

    // Clean up reference overlay
    this.removeReferenceOverlay();

    if (this.renderer) {
      this.renderer.dispose();
    }

    // Clear any render/resize hooks
    this._renderOverride = null;
    this._resizeHook = null;
    this._axisTriad?.dispose();
    this._axisTriad = null;

    // Clear resize tracking state
    this._lastAspect = null;
    this._lastContainerWidth = 0;
    this._lastContainerHeight = 0;

    this.container.innerHTML = '';

    console.log('[Preview] Resources disposed');
  }

  /**
   * Set an optional render override function
   * When set, this function is called instead of the default renderer.render()
   * @param {Function|null} fn - Override function or null to clear
   */
  setRenderOverride(fn) {
    this._renderOverride = typeof fn === 'function' ? fn : null;
  }

  /**
   * Clear the render override, restoring default rendering
   */
  clearRenderOverride() {
    this._renderOverride = null;
  }

  /**
   * Set an optional resize hook function
   * Called after the default resize handling completes
   * @param {Function|null} fn - Hook function receiving ({ width, height }) or null to clear
   */
  setResizeHook(fn) {
    this._resizeHook = typeof fn === 'function' ? fn : null;
  }

  /**
   * Clear the resize hook
   */
  clearResizeHook() {
    this._resizeHook = null;
  }

  /**
   * Set an optional post-load hook function
   * Called after an STL is successfully loaded
   * Useful for re-applying rotation centering after model reload
   * @param {Function|null} fn - Hook function or null to clear
   */
  setPostLoadHook(fn) {
    this._postLoadHook = typeof fn === 'function' ? fn : null;
  }

  /**
   * Clear the post-load hook
   */
  clearPostLoadHook() {
    this._postLoadHook = null;
  }

  /**
   * Register a listener invoked after every loadSTL / loadOFF completion.
   * Unlike setPostLoadHook (single-slot), multiple listeners can coexist.
   * @param {Function} fn
   */
  addPostLoadListener(fn) {
    if (typeof fn === 'function') this._postLoadListeners.push(fn);
  }

  /** @param {Function} fn */
  removePostLoadListener(fn) {
    this._postLoadListeners = this._postLoadListeners.filter((l) => l !== fn);
  }

  /** @private Fire all registered post-load listeners. */
  _firePostLoadListeners() {
    for (const fn of this._postLoadListeners) {
      try {
        fn();
      } catch (e) {
        console.error('[Preview] post-load listener error:', e);
      }
    }
  }

  /**
   * Register a listener invoked after every theme change. Used by
   * theme-sensitive overlays (e.g. axis distance markings) so they
   * can rebuild their textures with the new foreground color.
   * @param {Function} fn
   */
  addThemeChangeListener(fn) {
    if (typeof fn === 'function') this._themeChangeListeners.push(fn);
  }

  /** @param {Function} fn */
  removeThemeChangeListener(fn) {
    this._themeChangeListeners = this._themeChangeListeners.filter(
      (l) => l !== fn
    );
  }

  /** @private Fire all registered theme-change listeners. */
  _fireThemeChangeListeners() {
    for (const fn of this._themeChangeListeners) {
      try {
        fn();
      } catch (e) {
        console.error('[Preview] theme-change listener error:', e);
      }
    }
  }

  // --- Model Appearance Controls ---

  setModelOpacity(percent) {
    if (!this.appearanceOverrideEnabled) return;
    if (!this.mesh) return;
    const val = Math.max(10, Math.min(100, percent));
    const opacity = val / 100;

    const applyOpacity = (mat, meshObj) => {
      mat.transparent = opacity < 1;
      mat.opacity = opacity;
      mat.depthWrite = true;
      if (opacity < 1) meshObj.renderOrder = 1;
      mat.needsUpdate = true;
    };

    if (this.mesh.isGroup) {
      this.mesh.children.forEach((child) => {
        if (child.material) applyOpacity(child.material, child);
      });
    } else if (this.mesh.material) {
      applyOpacity(this.mesh.material, this.mesh);
    }
  }

  setBrightness(percent) {
    if (!this.appearanceOverrideEnabled) return;
    this._brightnessScale = percent / 100;
    this._applyLighting();
  }

  setContrast(percent) {
    if (!this.appearanceOverrideEnabled) return;
    this._contrastFactor = percent / 100;
    this._applyLighting();
  }

  _applyLighting() {
    if (!this.ambientLight) return;
    const bs = this._brightnessScale;
    const cf = this._contrastFactor;
    // Brightness scales all base intensities; contrast shifts ambient/directional ratio
    this.ambientLight.intensity =
      this.baseLightIntensities.ambient * bs * (2 - cf);
    this.directionalLight1.intensity = this.baseLightIntensities.dir1 * bs * cf;
    this.directionalLight2.intensity = this.baseLightIntensities.dir2 * bs * cf;
  }

  setAppearanceOverrideEnabled(enabled) {
    this.appearanceOverrideEnabled = enabled;
    this._syncAppearance();
  }

  _syncAppearance() {
    if (this.appearanceOverrideEnabled) {
      return;
    }
    // Reset to defaults when disabled — bypass the guard in setModelOpacity
    if (this.mesh) {
      const applyOpacity = (mat, meshObj) => {
        mat.transparent = false;
        mat.opacity = 1;
        mat.depthWrite = true;
        meshObj.renderOrder = 0;
        mat.needsUpdate = true;
      };
      if (this.mesh.isGroup) {
        this.mesh.children.forEach((child) => {
          if (child.material) applyOpacity(child.material, child);
        });
      } else if (this.mesh.material) {
        applyOpacity(this.mesh.material, this.mesh);
      }
    }
    this._brightnessScale = 1;
    this._contrastFactor = 1;
    this._applyLighting();
  }

  resetAppearance() {
    if (!this.appearanceOverrideEnabled) return;
    this.setModelOpacity(100);
    this._brightnessScale = 1;
    this._contrastFactor = 1;
    this._applyLighting();
  }

  // ── Rendered 2D Preview ───────────────────────────────────────────────────
  //
  // Displays worker-produced SVG output in a native browser SVG surface
  // inside the same preview container used by the 3D viewer.  This is
  // distinct from the reference overlay which is a Three.js texture plane.

  /**
   * Show a rendered 2D SVG preview, hiding the 3D canvas.
   *
   * The SVG is sanitized (scripts/event-handlers stripped) and inserted
   * into the dedicated #rendered2dPreview element.  When the SVG carries
   * only bare geometry (no fill/stroke), a desktop-parity stylesheet is
   * injected whose colors depend on the display mode:
   *
   *  - **draft** (F5-equivalent): muted sage green fill (#7A9F7A), no
   *    prominent outlines — matches desktop OpenSCAD's F5 Preview for 2D.
   *  - **rendered** (F6-equivalent): bright teal fill (#07D0A7) with red
   *    outlines (#FF0603) — matches desktop OpenSCAD's F6 Render for 2D.
   *
   * @param {string} svgText - Raw SVG markup from the worker
   * @param {{ mode?: 'draft'|'rendered' }} [options]
   */
  show2DPreview(svgText, options = {}) {
    const mode = options.mode || 'draft';
    const previewEl = document.getElementById('rendered2dPreview');
    if (!previewEl) return;

    const sanitized = PreviewManager.sanitizeSVG(svgText);

    previewEl.innerHTML = sanitized;

    const svgEl = previewEl.querySelector('svg');
    if (svgEl) {
      svgEl.removeAttribute('width');
      svgEl.removeAttribute('height');
      svgEl.style.maxWidth = '100%';
      svgEl.style.maxHeight = '100%';
      svgEl.style.width = 'auto';
      svgEl.style.height = 'auto';

      if (PreviewManager.svgLacksVisualStyling(svgEl)) {
        PreviewManager.injectDesktopParityStyling(svgEl, mode);
      }
    }

    previewEl.classList.remove('hidden');
    this._set2DPreviewActive(true);

    const summary = document.getElementById('previewModelSummary');
    if (summary) {
      summary.textContent =
        mode === 'rendered'
          ? 'Full-quality rendered 2D SVG preview is displayed.'
          : 'Draft 2D SVG preview is displayed.';
    }
  }

  /**
   * Display a 2D SVG as a flat plane inside the Three.js 3D viewport.
   *
   * Instead of hiding the 3D canvas and showing a flat HTML overlay,
   * this converts the SVG to a texture, creates a PlaneGeometry sized
   * to the SVG's physical mm dimensions, and adds it to the scene as
   * `this.mesh`.  The camera fits to the plane automatically and all
   * orbit/zoom/pan controls remain active — matching how desktop
   * OpenSCAD shows 2D geometry in its 3D viewport.
   *
   * @param {string} svgText - Raw (or pre-styled) SVG markup
   * @param {{ mode?: 'draft'|'rendered' }} [options]
   * @returns {Promise<void>}
   */
  async show2DPreviewAs3DPlane(svgText, options = {}) {
    if (!this.scene || !this.renderer) return;

    // Ensure any previous flat HTML 2D preview is dismissed
    this.hide2DPreview();

    // Remove existing 3D mesh
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this._disposeMeshResources();
      this.mesh = null;
    }

    // Render SVG to a high-resolution canvas texture (4096px longest edge)
    // while preserving the original SVG width/height attributes for correct
    // mm dimension extraction. The PlaneGeometry is then sized in mm to
    // guarantee 1:1 scale fidelity with the 3D mesh.
    const { texture, widthMm, heightMm } = await this.svgTextToCanvasTexture(
      svgText,
      null,
      { targetCanvasSize: 4096 }
    );

    const planeW = widthMm || 200;
    const planeH = heightMm || 150;

    const geometry = new PlaneGeometry(planeW, planeH);
    const material = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: DoubleSide,
      depthWrite: true,
    });

    this.mesh = new Mesh(geometry, material);
    this.mesh.name = '2dPreviewPlane';
    this.scene.add(this.mesh);

    this.fitCameraToModel();
    if (this.renderer) this.renderer.render(this.scene, this.getActiveCamera());

    // Update ARIA summary
    const summary = document.getElementById('previewModelSummary');
    if (summary) {
      const mode = options.mode || 'draft';
      summary.textContent =
        mode === 'rendered'
          ? 'Full-quality rendered 2D SVG preview displayed in 3D viewport.'
          : 'Draft 2D SVG preview displayed in 3D viewport.';
    }
  }

  /**
   * Hide the rendered 2D preview, restoring the 3D canvas.
   */
  hide2DPreview() {
    const previewEl = document.getElementById('rendered2dPreview');
    if (!previewEl) return;
    previewEl.classList.add('hidden');
    previewEl.innerHTML = '';
    this._set2DPreviewActive(false);
  }

  /**
   * Toggle visibility of 3D canvas vs 2D preview surface.
   * @param {boolean} is2D
   */
  _set2DPreviewActive(is2D) {
    this._is2DPreviewActive = is2D;

    // While the drawing editor owns the area, a render behind it must not
    // put the canvas or the label back: what it asked for is remembered
    // above and honored when the editor gives the area back.
    if (this._isEditorSurfaceActive) return;

    // Hide 3D canvas when 2D is active, show when 3D
    if (this.renderer?.domElement) {
      this.renderer.domElement.style.display = is2D ? 'none' : '';
    }

    // Hide placeholder when any content is active
    const placeholder = this.container?.querySelector('.preview-placeholder');
    if (placeholder) {
      placeholder.style.display = is2D || this.mesh ? 'none' : '';
    }

    // Update container ARIA label
    if (this.container) {
      this.container.setAttribute(
        'aria-label',
        is2D ? 'Rendered 2D SVG preview' : '3D model preview and controls'
      );
    }
  }

  /**
   * Give the preview area over to the drawing editor.
   *
   * The editor goes where the 3D view is, not in a panel beside it and not in
   * a block inside the customizer, because it is the biggest surface on the
   * page and on a phone it is the whole of it. Same shape as the 2D preview's
   * takeover: hide the canvas, hide the placeholder, say what the region is
   * now.
   *
   * And stand the camera keys down. `keyboardHandler` fires for any keydown
   * while focus is inside #previewContainer unless the target is an INPUT,
   * TEXTAREA, SELECT or contentEditable, so a focused table row or a button
   * inside the editor would rotate the model behind it while a person thought
   * they were moving between regions.
   *
   * @param {HTMLElement} el - The editor's own root, already in the container
   */
  showEditorSurface(el) {
    if (!el) return;
    this._isEditorSurfaceActive = true;
    el.hidden = false;
    el.classList.remove('hidden');
    // The render-state pills (the ready badge, the generating pill, the
    // status bar) float over this area and, at 412 px wide, over
    // the editor's own title. They fade while the editor has the area; they
    // stay in the accessibility tree, so what they announce is still heard.
    this.container
      ?.closest('.preview-canvas-section')
      ?.classList.add('has-drawing-editor');
    if (this.renderer?.domElement) {
      this.renderer.domElement.style.display = 'none';
    }
    const placeholder = this.container?.querySelector('.preview-placeholder');
    if (placeholder) placeholder.style.display = 'none';
    const twoD = document.getElementById('rendered2dPreview');
    if (twoD) twoD.classList.add('hidden');
    if (this.container) {
      this.container.setAttribute('aria-label', 'Drawing editor');
      // The container's own tabindex would put an extra stop in front of the
      // editor's toolbar for no reason: inside the editor, the editor's
      // controls are the tab stops.
      this.container.removeAttribute('tabindex');
    }
  }

  /**
   * Give the preview area back, and put the camera keys back with it.
   *
   * @param {HTMLElement} el - The editor's own root
   */
  hideEditorSurface(el) {
    this._isEditorSurfaceActive = false;
    if (el) {
      el.hidden = true;
      el.classList.add('hidden');
    }
    this.container
      ?.closest('.preview-canvas-section')
      ?.classList.remove('has-drawing-editor');
    // Whatever was asked for while the editor had the area - a 2D preview,
    // a mesh, nothing yet - is what comes back.
    const twoD = document.getElementById('rendered2dPreview');
    if (twoD) twoD.classList.toggle('hidden', !this._is2DPreviewActive);
    if (this.container) this.container.setAttribute('tabindex', '0');
    this._set2DPreviewActive(this._is2DPreviewActive === true);
  }

  /**
   * Show or hide what the model looks like while the editor still owns the
   * area (the editor's Charm view).
   *
   * The editor's Charm view is not a picture the editor draws. It is this
   * preview, which `showEditorSurface` put out of sight, and showing it again
   * is the whole of it. Deliberately narrow: it does nothing unless the
   * editor is active, and it never decides between the 3D canvas and the 2D
   * preview - it honors whichever one was already asked for, so a design
   * being previewed as SVG shows its SVG.
   *
   * @param {boolean} visible
   * @param {HTMLElement} [host] - The box the charm has to fit inside
   */
  setEditorCharmVisible(visible, host = null) {
    if (!this._isEditorSurfaceActive) return;
    const show3D = visible && !this._is2DPreviewActive;
    if (this.renderer?.domElement) {
      this.renderer.domElement.style.display = show3D ? '' : 'none';
    }
    const twoD = document.getElementById('rendered2dPreview');
    if (twoD) {
      twoD.classList.toggle(
        'hidden',
        !(visible && this._is2DPreviewActive === true)
      );
    }
    this._charmHost = show3D ? host : null;
    if (show3D) this._sizeCanvasToCharmHost();
    else this._restoreCanvasBox();
  }

  /**
   * Fit the canvas to the part of the area a person can actually see.
   *
   * The editor is laid over this canvas, and its toolbar and drawer cover a
   * great deal of it. Measured with the charm view open, visible area against
   * canvas area: 66 per cent at 1280, 57 at 900, **39 at 412** - and the
   * shapes differ as much as the sizes do, because at 900 the canvas is 476 by
   * 761 while the window left over is 460 by 445. Framed for the canvas, the
   * charm is drawn to fill a tall box and the near-square window shows a band
   * across its middle: a slab, not a charm.
   *
   * So the canvas is given the window's box while the charm view is on, and
   * its own back when it is over.
   */
  _sizeCanvasToCharmHost() {
    const el = this.renderer?.domElement;
    const host = this._charmHost;
    if (!el || !host || !this.container) return;
    const area = this.container.getBoundingClientRect();
    const box = host.getBoundingClientRect();
    const width = Math.max(1, Math.round(box.width));
    const height = Math.max(1, Math.round(box.height));
    el.style.position = 'absolute';
    el.style.left = `${Math.round(box.left - area.left)}px`;
    el.style.top = `${Math.round(box.top - area.top)}px`;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    // updateStyle false: the CSS box is set above and three.js must not take
    // it back, or the canvas would be stretched to the container again.
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Give the canvas the whole area back. */
  _restoreCanvasBox() {
    const el = this.renderer?.domElement;
    if (!el) return;
    el.style.position = '';
    el.style.left = '';
    el.style.top = '';
    el.style.width = '';
    el.style.height = '';
    // handleResize does nothing when the dimensions it reads are unchanged,
    // and from its point of view they never changed - it was the canvas that
    // moved, not the container. Forgetting what it last saw is what makes it
    // do the work.
    this._lastContainerWidth = null;
    this._lastContainerHeight = null;
    this.handleResize?.();
  }

  /** Whether the drawing editor currently owns the preview area. */
  isEditorSurfaceActive() {
    return this._isEditorSurfaceActive === true;
  }

  /**
   * Sanitize SVG to prevent XSS — strips scripts, event handlers,
   * and external references while preserving geometry and styling.
   * @param {string} svgText
   * @returns {string}
   */
  static sanitizeSVG(svgText) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    const svg = doc.documentElement;

    // Remove elements that can embed arbitrary HTML or external content
    for (const el of [
      ...svg.querySelectorAll('script, foreignObject, iframe, embed, object'),
    ]) {
      el.remove();
    }

    // Block <use> elements with external references (SSRF / data exfiltration)
    for (const useEl of [...svg.querySelectorAll('use')]) {
      const href =
        useEl.getAttribute('href') || useEl.getAttribute('xlink:href') || '';
      const val = href.trim().toLowerCase();
      if (
        val.startsWith('http:') ||
        val.startsWith('https:') ||
        val.startsWith('//')
      ) {
        useEl.remove();
      }
    }

    // Remove event-handler attributes and dangerous URI schemes from all elements
    const allEls = svg.querySelectorAll('*');
    for (const el of allEls) {
      for (const attr of [...el.attributes]) {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
        if (attr.name === 'href' || attr.name === 'xlink:href') {
          const val = attr.value.trim().toLowerCase();
          if (val.startsWith('javascript:') || val.startsWith('data:')) {
            el.removeAttribute(attr.name);
          }
        }
      }
    }

    return svg.outerHTML;
  }

  /**
   * Detect whether an SVG element's geometry paths lack explicit
   * fill/stroke styling (geometry-only export from OpenSCAD).
   * @param {SVGElement} svgEl
   * @returns {boolean}
   */
  static svgLacksVisualStyling(svgEl) {
    const shapes = svgEl.querySelectorAll(
      'path, polygon, polyline, line, circle, ellipse, rect'
    );
    if (shapes.length === 0) return false;

    let unstyled = 0;
    for (const shape of shapes) {
      const fill = shape.getAttribute('fill');
      const stroke = shape.getAttribute('stroke');
      const style = shape.getAttribute('style') || '';
      const hasFill = fill && fill !== 'none';
      const hasStroke = stroke && stroke !== 'none';
      const hasStyleFill =
        style.includes('fill:') && !style.includes('fill:none');
      const hasStyleStroke =
        style.includes('stroke:') && !style.includes('stroke:none');
      if (!hasFill && !hasStroke && !hasStyleFill && !hasStyleStroke) {
        unstyled++;
      }
    }
    return unstyled > shapes.length / 2;
  }

  /**
   * Inject a preview-only <style> element that approximates desktop
   * OpenSCAD's 2D viewport appearance.  Colors are selected per mode:
   *
   *  - **draft** (F5 Preview): #7A9F7A sage green fill, subtle edges.
   *  - **rendered** (F6 Render): #07D0A7 teal fill, #FF0603 red outlines.
   *
   * @param {SVGElement} svgEl
   * @param {'draft'|'rendered'} [mode='draft']
   */
  static injectDesktopParityStyling(svgEl, mode = 'draft') {
    const ns = 'http://www.w3.org/2000/svg';
    const existing = svgEl.querySelector('style[data-forge-preview]');
    if (existing) return;

    const palette = get2DStylePalette(mode);

    const styleEl = document.createElementNS(ns, 'style');
    styleEl.setAttribute('data-forge-preview', 'true');
    styleEl.textContent = [
      'path, polygon, polyline, circle, ellipse, rect {',
      `  fill: ${palette.fill};`,
      `  stroke: ${palette.stroke};`,
      `  stroke-width: ${palette.strokeWidth};`,
      `  fill-opacity: ${palette.fillOpacity};`,
      '}',
      'line {',
      `  stroke: ${palette.stroke};`,
      `  stroke-width: ${palette.strokeWidth};`,
      '}',
    ].join('\n');
    svgEl.insertBefore(styleEl, svgEl.firstChild);
  }
}
